<template>
  <div class="max-w-7xl mx-auto mt-8 bg-white p-6 rounded-xl shadow-md">
    <!-- Header -->
    <div class="flex flex-col gap-3 md:flex-row md:items-center md:justify-between mb-2">
      <h2 class="text-2xl font-bold text-gray-800">Nachrichten</h2>

      <div class="flex flex-wrap items-center gap-3">
        <BaseButton variant="switch" :disabled="triggeringBot" @click="triggerBotSync">
          {{ triggeringBot ? "Wird geholt …" : (botTriggerMessage || "Nachrichten holen") }}
        </BaseButton>

        <!-- Edit Prompt button -->
        <button
            type="button"
            class="px-3 py-2 rounded-md border border-gray-300 bg-white text-gray-800 hover:bg-gray-50 hover:border-gray-400 transition text-sm"
            @click="openSystemSettingsModal"
            title="Prompts für die asynchrone Bewertung bearbeiten"
        >
          Prompt bearbeiten
        </button>

        <!-- ✅ Info Popover (compact + cleaner) -->
        <div class="relative" ref="infoPopoverRoot">
          <button
              type="button"
              class="w-9 h-9 inline-flex items-center justify-center rounded-md border border-gray-300 bg-white text-gray-800 hover:bg-gray-50 hover:border-gray-400 transition"
              @click="toggleInfoPopover"
              aria-label="Info"
              :aria-expanded="showInfoPopover ? 'true' : 'false'"
              title="Wie diese Seite funktioniert"
          >
            <span class="text-sm font-bold leading-none">i</span>
          </button>

          <div v-if="showInfoPopover" class="absolute right-0 mt-2 w-[360px] max-w-[88vw] z-40">
            <div class="bg-white border border-gray-200 rounded-xl shadow-xl overflow-hidden">
              <!-- Header -->
              <div class="px-4 py-3 border-b border-gray-200 bg-gray-50 flex items-center justify-between">
                <div>
                  <div class="text-sm font-semibold text-gray-900">Nachrichten – Info</div>
                  <div class="text-[11px] text-gray-500">Sessions, Filter, Freigabe</div>
                </div>

                <button
                    type="button"
                    class="w-8 h-8 inline-flex items-center justify-center rounded-md border border-gray-300 bg-white hover:bg-gray-50 text-gray-700 transition"
                    @click="closeInfoPopover"
                    aria-label="Close"
                    title="Close"
                >
                  <span class="text-lg leading-none">×</span>
                </button>
              </div>

              <!-- Body -->
              <div class="p-4 text-xs text-gray-700 space-y-3 max-h-[55vh] overflow-y-auto">
                <!-- Sessions -->
                <div>
                  <div class="flex items-center gap-2 mb-1">
                    <span
                        class="px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-700 border border-indigo-100 text-[11px] font-semibold"
                    >
                      Sessions
                    </span>
                    <span class="text-[11px] text-gray-500">Ohne Session keine Bewertung</span>
                  </div>

                  <ul class="list-disc pl-5 space-y-1 text-gray-600">
                    <li><b>Session wählen</b> — erst dann gibt es Kriterien zu bewerten.</li>
                    <li>Die Kriterien erscheinen <b>seitenweise</b> in einem Fenster.</li>
                  </ul>
                </div>

                <div class="border-t border-gray-200"></div>

                <!-- Review -->
                <div>
                  <div class="flex items-center gap-2 mb-1">
                    <span
                        class="px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-100 text-[11px] font-semibold"
                    >
                      Freigabe &amp; Neubewertung
                    </span>
                    <span class="text-[11px] text-gray-500">Zustand einer Nachricht</span>
                  </div>

                  <ul class="list-disc pl-5 space-y-1 text-gray-600">
                    <li><b>Freigeben</b> = abgeschlossen; die Nachricht wird neu vektorisiert.</li>
                    <li>
                      <b>Neu bewerten</b> (Kreispfeil) = alle vorhandenen Bewertungen löschen und
                      die Nachricht zurück in die Bewertung geben; eine freigegebene wird dabei
                      wieder geöffnet. Die gelöschten Werte bleiben im Archiv.
                    </li>
                    <li>
                      Beides ist <b>unabhängig</b>: neu bewerten geht auch ohne Freigabe — und
                      genau dort wird es gebraucht, wenn nach einer Korrektur nur noch
                      menschliche Werte stehen und der RatingWorker deshalb nicht mehr greift.
                    </li>
                  </ul>
                </div>

                <div class="border-t border-gray-200"></div>

                <!-- Filters -->
                <div>
                  <div class="flex items-center gap-2 mb-1">
                    <span
                        class="px-2 py-0.5 rounded-full bg-amber-50 text-amber-800 border border-amber-100 text-[11px] font-semibold"
                    >
                      Filter & Fortschritt
                    </span>
                    <span class="text-[11px] text-gray-500">Die Zahlen folgen den Filtern</span>
                  </div>

                  <ul class="list-disc pl-5 space-y-1 text-gray-600">
                    <li>Filter wirken auf <b>Liste und Fortschritt</b>.</li>
                    <li><b>Freigegebene ausblenden</b> blendet nur aus — der Fortschritt bleibt.</li>
                    <li>
                      Der Balken zählt <b>Nachrichten</b>, die Pillen darunter zählen
                      <b>Zellen</b>.
                    </li>
                    <li>
                      Der <b>Zustandsfilter</b> verkleinert nur die Liste — die Pillen
                      zeigen weiter das ganze Bild.
                    </li>
                  </ul>
                </div>

                <div class="border-t border-gray-200"></div>

                <!-- Metadata -->
                <div>
                  <div class="flex items-center gap-2 mb-1">
                    <span
                        class="px-2 py-0.5 rounded-full bg-slate-50 text-slate-700 border border-slate-200 text-[11px] font-semibold"
                    >
                      Kennzeichen
                    </span>
                    <span class="text-[11px] text-gray-500">Die kleinen Marken</span>
                  </div>

                  <ul class="list-disc pl-5 space-y-1 text-gray-600">
                    <li><b>Vectorized</b> = steht dem RAG als Beispiel zur Verfügung.</li>
                    <li><b>Reply ↩</b> springt zur Bezugsnachricht, falls es eine gibt.</li>
                  </ul>
                </div>

                <div class="border-t border-gray-200"></div>

                <!-- Tastenkuerzel.
                     ACHTUNG: die Liste wird NICHT hier gepflegt, sondern in
                     async-sessions/shortcuts.js. Vorher stand sie doppelt --
                     hier englisch und im Editor deutsch -- und beide Fassungen
                     waren unvollstaendig und widersprachen sich. -->
                <div>
                  <div class="flex items-center gap-2 mb-1">
                    <span
                        class="px-2 py-0.5 rounded-full bg-gray-50 text-gray-700 border border-gray-200 text-[11px] font-semibold"
                    >
                      Tastenkürzel
                    </span>
                    <span class="text-[11px] text-gray-500">Navigation & Bewertung</span>
                  </div>

                  <div class="space-y-2">
                    <div v-for="gruppe in shortcutGruppen" :key="gruppe.titel">
                      <div class="font-semibold text-gray-700">{{ gruppe.titel }}</div>
                      <ul class="mt-1 space-y-1">
                        <li
                            v-for="(eintrag, i) in gruppe.eintraege"
                            :key="i"
                            class="flex items-start gap-2"
                        >
                          <span class="shrink-0 flex items-center gap-0.5">
                            <span v-for="taste in eintrag.tasten" :key="taste" class="kbd">
                              {{ taste }}
                            </span>
                          </span>
                          <span class="text-gray-600">{{ eintrag.wirkung }}</span>
                        </li>
                      </ul>
                    </div>
                  </div>

                  <p class="mt-2 text-[11px] text-gray-500">
                    Die Bewertungsskala reicht von {{ skalaText }}. Eingaben außerhalb
                    werden auf dieses Band begrenzt.
                  </p>
                </div>
              </div>

              <!-- Footer -->
              <div class="px-4 py-3 border-t border-gray-200 bg-gray-50">
                <div class="text-[11px] text-gray-500">
                  Ablauf: Session wählen → bewerten → freigeben.
                </div>
              </div>
            </div>
          </div>
        </div>
        <!-- /Info Popover -->

        <div class="flex items-center space-x-3">
          <span class="text-sm text-gray-600 bg-gray-100 px-2.5 py-1 rounded-full">
            Freigegeben: {{ reviewedCount }}
          </span>
          <span class="text-sm text-gray-600 bg-gray-100 px-2.5 py-1 rounded-full">
            Offen: {{ unreviewedCount }}
          </span>
        </div>

        <BaseToggle v-model="hideReviewed" label="Freigegebene ausblenden" />
      </div>
    </div>

    <!-- Last update -->
    <!-- Der Nachladezustand steht HIER und nicht an der Liste: er sagt etwas
         über die Aktualität des Gezeigten, und das ist genau die Frage, die
         diese Zeile ohnehin beantwortet. Die Liste selbst bleibt unangetastet
         stehen — ein Stand, der gleich ersetzt wird, ist immer noch der letzte
         belegte.

         ⚠️ Bedingung ist `loading && messages.length`, nicht `loading` allein:
         bei der Erstladung steht hier noch gar kein Stand, den man
         aktualisieren könnte. Dort trägt die Aussage der Block bei der Liste. -->
    <div class="text-sm text-gray-500 mb-4">
      Zuletzt aktualisiert:
      <span v-if="formattedLastUpdate">{{ formattedLastUpdate }}</span>
      <span v-else>–</span>
      <span
          v-if="loading && messages.length"
          class="ml-2 inline-flex items-center gap-1.5 text-gray-400"
          role="status"
          aria-live="polite"
      >
        <span class="w-1.5 h-1.5 rounded-full bg-gray-400 animate-pulse" aria-hidden="true"></span>
        {{ LADEN.nachladen }}
      </span>
    </div>

    <!-- ✅ Session selector + End button -->
    <!-- ✅ CHANGED: wrapped in .filter-area so arrow keys can be blocked there -->
    <div class="filter-area mb-4 flex items-end gap-3">
      <div class="flex-1">
        <BaseSelect
            v-model="selectedSessionId"
            :options="sessionOptions"
            :showPlaceholder="true"
            placeholder="Session wählen"
        />
      </div>

      <BaseButton
          v-if="selectedSessionId"
          variant="cancel"
          :disabled="isEndingSession || selectedSessionEnded"
          @click="endSelectedSession"
      >
        {{ selectedSessionEnded ? "Session beendet" : (isEndingSession ? "Wird beendet …" : "Session beenden") }}
      </BaseButton>
    </div>

    <!-- UMFANGSZEILE (01.10.): welcher Server gerade zählt -- sonst nichts.
         Eine Zuordnung gilt immer für den AKTIVEN Server des Credentials;
         liegen unter denselben Kategorien Nachrichten auf ANDEREN Servern,
         sagt das ein Icon mit Zahl je Server (sie sind gespeichert und bei
         erneuter Wahl des Servers wieder sichtbar). Kein Link: umstellen
         kann nur der Admin. fixiert: main scrollt (App.vue, overflow-auto).
         Aus GET /session-categories/andere-server.
         Eine Session OHNE Credential zeigt gar nichts -- „Kein Server"
         samt DISCORD_SERVER.keiner wäre dort falsch; die Zeile entfällt. -->
    <p v-if="umfangZeigen" class="umfangszeile mb-3 flex items-center gap-1.5 text-xs text-gray-600">
      <span v-if="andere.server">
        Server „{{ andere.server.guild_name || andere.server.guild_id }}“
      </span>
      <template v-else>
        <span>Kein Server</span>
        <BaseInfoIcon :text="DISCORD_SERVER.keiner" label="Kein Server" fixiert />
      </template>
      <BaseInfoIcon
        v-if="andere.andere.length"
        :text="andereServerText"
        label="Andere Server"
        fixiert
      />
    </p>

    <!-- Hint when no session is selected. Sichtbar bleibt, was zuerst zu
         tun ist; das Warum steht seit dem 30.09. am Icon (Simon: redundanten
         Text in Tooltips). fixiert: main scrollt (App.vue, overflow-auto). -->
    <div
        v-if="!selectedSessionId"
        class="mb-4 flex items-center gap-2 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2"
    >
      <span>{{ ERKLAERUNG.sessionWaehlen }}</span>
      <BaseInfoIcon :text="ERKLAERUNG.sessionWaehlenMehr" label="Session wählen" ton="amber" fixiert />
    </div>

    <!-- Filter Bar -->
    <!-- ✅ CHANGED: wrapped in .filter-area -->
    <div class="filter-area grid grid-cols-1 md:grid-cols-5 gap-3 mb-2">
      <BaseSelect v-model="filterAuthorId" :options="authorOptions" :showPlaceholder="true" placeholder="Verfasser wählen" />
      <BaseSelect v-model="filterTeamId" :options="teamOptions" :showPlaceholder="true" placeholder="Team wählen" />
      <BaseSelect v-model="filterChannel" :options="channelOptions" :showPlaceholder="true" placeholder="Kanal wählen" />
      <BaseSelect
          v-model="filterCategory"
          :options="categoryOptions"
          :showPlaceholder="true"
          placeholder="Kategorie wählen"
      />
      <BaseSelect
          v-model="filterReviewState"
          :options="reviewStateOptions"
          :showPlaceholder="false"
      />
    </div>

    <!-- Hinweise ohne Entscheidung. Ersetzt die alert()-Aufrufe dieser Seite.
         ACHTUNG: bewusst fixiert und nicht mehr im Seitenfluss. Der Streifen
         stand frueher ganz oben - wer bei Nachricht 40 arbeitet, hat eine
         Meldung dort nie gesehen. Fixiert braucht es dafuer auch keine zweite
         klebende Leiste neben der Kriterienzeile (die haelt top-0 / z-20). -->
    <div
        v-if="noticeText"
        class="fixed bottom-4 left-1/2 -translate-x-1/2 z-[45] max-w-[90vw]"
        role="status"
        aria-live="polite"
    >
      <div
          class="text-sm rounded-md shadow-lg border px-3 py-2 flex items-start gap-3"
          :class="noticeTone === 'error'
            ? 'text-red-800 bg-red-50 border-red-300'
            : 'text-amber-800 bg-amber-50 border-amber-300'"
      >
        <span>{{ noticeText }}</span>
        <button
            type="button"
            class="shrink-0 text-xs px-2 py-1 rounded-md border bg-white transition"
            :class="noticeTone === 'error'
              ? 'border-red-300 hover:bg-red-100'
              : 'border-amber-300 hover:bg-amber-100'"
            @click="hideNotice"
            aria-label="Hinweis schließen"
            title="Schließen"
        >
          Schließen
        </button>
      </div>
    </div>


    <!-- ✅ Progress bar (filter-dependent) -->
    <div class="mb-3">
      <div class="flex items-center justify-between text-xs text-gray-600 mb-1">
        <span class="flex items-center gap-1.5">
          Fortschritt (mit den aktuellen Filtern)
          <BaseInfoIcon :text="ERKLAERUNG.fortschritt" label="Fortschritt" fixiert />
        </span>
        <span class="font-medium">
          {{ progressReviewed }} / {{ progressTotal }} freigegeben ({{ progressPercent }} %)
        </span>
      </div>

      <div class="w-full bg-gray-200 rounded-full h-2.5">
        <div
            class="bg-indigo-600 h-2.5 rounded-full transition-all duration-300"
            :style="{ width: progressTotal > 0 ? (progressReviewed / progressTotal * 100) + '%' : '0%' }"
        ></div>
      </div>

      <!-- Zellen statt Nachrichten. Der Balken darueber sagt, wie viele
           Nachrichten abgehakt sind; er sagt nichts darueber, ob dabei etwas
           korrigiert wurde. -->
      <div class="mt-2 flex flex-wrap items-center gap-2 text-[11px]">
        <span class="px-2 py-0.5 rounded-full bg-gray-100 text-gray-700 border border-gray-200">
          {{ zellStatistik.zellen }} bewertete Zellen
        </span>
        <span
            class="px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-700 border border-indigo-200"
            title="Zellen, die ein Mensch überschrieben hat (score_source = human)."
        >
          {{ zellStatistik.human }} korrigiert
        </span>
        <span
            class="px-2 py-0.5 rounded-full bg-amber-50 text-amber-800 border border-amber-200"
            title="Korrigierte Zellen, deren Wert vom gesicherten Modellwert abweicht."
        >
          {{ zellStatistik.abweichend }} Modell ≠ Mensch
        </span>
        <span
            v-if="zellStatistik.humanOhneModell"
            class="px-2 py-0.5 rounded-full bg-gray-50 text-gray-500 border border-gray-200"
            title="Korrigiert, aber ohne gesicherten Modellwert — für diese lässt sich kein Delta rechnen."
        >
          {{ zellStatistik.humanOhneModell }} ohne Modellwert
        </span>
      </div>

      <!-- Der Dauersatz „Die Zahlen folgen den Filtern … zählen ZELLEN"
           steht seit dem 30.09. am Icon neben „Fortschritt". Sichtbar
           bleiben nur die zwei Sätze, die einen gerade AKTIVEN Schalter
           benennen -- sie sagen, warum Liste und Zahlen auseinanderlaufen. -->
      <div v-if="hideReviewed || filterReviewState" class="mt-1 text-[11px] text-gray-500">
        <span v-if="hideReviewed">Die Liste blendet freigegebene Nachrichten aus.</span>
        <span v-if="filterReviewState">
          Der Zustandsfilter verkleinert nur die Liste, nicht die Zahlen darüber.
        </span>
      </div>
    </div>

    <!-- Criteria window info + Paging (only if criteria exist) -->
    <!-- Die Leiste klebt oben. Sie ist der einzige Kopfteil, den man waehrend
         des Bewertens noch braucht - deshalb darf die Liste darunter frei im
         Seitenfluss laufen, statt in einem eigenen Scrollkaefig zu sitzen. -->
    <div
        v-if="effectiveCriterias.length"
        class="sticky top-0 z-20 -mx-6 mb-2 px-6 py-2 bg-white border-b border-gray-100
               flex items-center justify-between text-xs text-gray-500"
    >
      <span>Kriterienfenster {{ currentWindowIndex }} von {{ totalWindows }}</span>

      <div class="flex items-center gap-2">
        <button
            type="button"
            class="
            flex items-center justify-center
            w-10 h-10
            rounded-md border border-gray-300
            bg-white text-gray-800
            hover:bg-gray-100 hover:border-gray-400
            focus:outline-none focus:ring-2 focus:ring-indigo-400
            disabled:opacity-40 disabled:cursor-not-allowed
            transition
          "
            :disabled="!canPagePrev"
            @click="pagePrev"
            aria-label="Vorheriges Kriterienfenster"
            title="Vorheriges Fenster"
        >
          <span class="text-2xl font-extrabold leading-none">‹</span>
        </button>

        <button
            type="button"
            class="
            flex items-center justify-center
            w-10 h-10
            rounded-md border border-gray-300
            bg-white text-gray-800
            hover:bg-gray-100 hover:border-gray-400
            focus:outline-none focus:ring-2 focus:ring-indigo-400
            disabled:opacity-40 disabled:cursor-not-allowed
            transition
          "
            :disabled="!canPageNext"
            @click="pageNext"
            aria-label="Nächstes Kriterienfenster"
            title="Nächstes Fenster"
        >
          <span class="text-2xl font-extrabold leading-none">›</span>
        </button>
      </div>
    </div>

    <!-- Merkliste. Steht ÜBER der Liste und nicht darunter: sie ist die Klammer
         um das, was man beim Durchgehen sammelt, und wer scrollt, soll den
         Zähler nicht suchen müssen.

         Erscheint erst ab dem ersten gemerkten Eintrag — ein leerer Streifen
         wäre ein Dauerbanner für eine Funktion, die die meisten Sitzungen gar
         nicht brauchen. -->
    <div
        v-if="merkAnzahl > 0"
        class="mb-3 flex flex-wrap items-center gap-x-3 gap-y-1 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm"
    >
      <span class="font-semibold text-amber-900">
        ★ {{ merkAnzahl }} {{ merkAnzahl === 1 ? "Nachricht" : "Nachrichten" }} gemerkt
      </span>
      <!-- ⚠️ Hier stand bis zum 18.08. ein Erklaersatz ("Auf der
           Reflexionsseite wird daraus ein Vergleichssatz — aufgefuellt wird
           auf die dort eingetragene Zielgroesse"). Ersatzlos gestrichen,
           Entscheidung Simon: der Streifen steht dauerhaft ueber der Liste,
           sobald etwas gemerkt ist, und eine Erklaerung an dieser Stelle
           liest man einmal und danach nie wieder. Der Verweis darunter
           sagt weiterhin, WOHIN die Liste fuehrt -- das ist das Noetige. -->
      <router-link
          to="/async/reflection"
          class="text-xs text-indigo-700 underline hover:text-indigo-900"
      >
        Zur Reflexion
      </router-link>
      <button
          type="button"
          class="ml-auto text-xs text-amber-900 underline hover:text-amber-950"
          @click="merklisteLeeren"
      >
        alle vergessen
      </button>
    </div>

    <!-- Message list -->
    <!-- ACHTUNG: hier stand frueher max-h-[70vh] overflow-y-auto. Das erzeugte
         einen zweiten Scrollbalken innerhalb der ohnehin scrollenden Seite
         (App.vue: main ist overflow-auto h-screen). Die Liste laeuft jetzt im
         Seitenfluss; scroll-margin (siehe <style>) haelt angesprungene Karten
         unter der klebenden Kriterienleiste. -->
    <div
        ref="messagesContainer"
        class="messages-flow space-y-3"
        :class="{ 'focus-mode': editorOpenForId !== null }"
    >
      <!-- ACHTUNG: der :key traegt criteriaWindowStart NICHT mehr. Vorher
           erzeugte jedes Blaettern im Kriterienfenster neue Instanzen aller
           Karten und vernichtete deren data() - offener Editor, gemerkter
           Kriterienindex, ungespeicherte Eingaben. visibleCriterias ist eine
           reaktive Prop; die Karte zeichnet auch ohne Remount neu. -->
      <MessageCard
          v-for="m in visibleMessages"
          :key="m.id"
          :message="m"
          :allCriterias="effectiveCriterias"
          :visibleCriterias="windowedCriterias"
          :isActive="m.id === activeMessageId"
          :suspendKeys="confirmState.open || showSystemSettingsModal"
          :onScrollToMessage="scrollToMessage"
          :ratingStatus="ratingStatus"
          :ragStatus="ragStatus"
          :class="editorOpenForId !== null && editorOpenForId !== m.id ? 'card-dimmed' : null"
          @approve="approve"
          @approveBlocked="onApproveBlocked"
          @rate="rate"
          @rateAgain="rateAgain"
          @delete="deleteMsg"
          @requestFocus="setActiveMessage(m.id)"
          @requestCriteriaPage="onRequestCriteriaPage"
          @editorState="onEditorState"
          @notice="showNotice($event.text, $event.tone)"
          :ref="(el) => setMessageRef(m.id, el)"
      />
    </div>

    <!-- DREI Zustände unter der Liste, und die Reihenfolge trägt die Aussage.

         ⚠️ Der Fehler steht VORN. Bis zum 24.08. gab es diesen Zweig nicht, und
         nach einem fehlgeschlagenen Ladevorgang war `!loading &&
         messages.length === 0` wahr — die Seite behauptete „Keine Nachrichten
         gefunden.", also etwas über den Bestand, das sie nie erfahren hat.
         Steht der Fehler nicht vor dem Leertext, kehrt genau das zurück.

         ⚠️ Der Leertext gilt nur nach einer ERFOLGREICHEN Ladung. Deshalb
         `ladeFehler === null` und nicht `!ladeFehler`: ein Fehlschlag ohne
         lesbaren Grund ist "" und damit falsy — er würde den Leertext sonst
         wieder freigeben, und zwar ausgerechnet im undurchsichtigsten Fall. -->
    <p
        v-if="ladeFehler !== null && messages.length === 0"
        class="text-red-800 bg-red-50 border border-red-200 rounded-md px-3 py-2 mt-4 text-center text-sm"
        role="alert"
    >
      {{ LADEN.fehler }}<span v-if="ladeFehler"> {{ ladeFehler }}</span>
    </p>

    <p
        v-else-if="loading && messages.length === 0"
        class="text-gray-500 mt-4 text-center flex items-center justify-center gap-2"
        role="status"
        aria-live="polite"
    >
      <span class="w-2 h-2 rounded-full bg-gray-400 animate-pulse" aria-hidden="true"></span>
      {{ LADEN.erstladung }}
    </p>

    <!-- Steht VOR dem allgemeinen Leertext, und die Reihenfolge ist die Aussage:
         bei gewählter Session ist eine leere Liste eine RICHTIGE Antwort über DIESE
         Session, nicht über den Bestand. "Keine Nachrichten gefunden." wäre hier eine
         Behauptung über 228 Zeilen, von denen die Seite gerade keine einzige
         abgefragt hat. -->
    <!-- Die gewählte Session ist KEINE asynchrone: die Auswahl zeigt alle
         Sessions des Credentials, eine Live-Session hat keine Zuordnung und
         damit nie Nachrichten. Ohne diesen Zweig hieße es nur „leer", und
         der Grund bliebe offen (18.09.). -->
    <p
        v-else-if="selectedSessionId && messages.length === 0 && selectedSessionObj && selectedSessionObj.is_async === false"
        class="text-gray-500 mt-4 text-center leertext-live"
    >
      {{ TREFFER.liveSession }}
    </p>

    <p
        v-else-if="selectedSessionId && messages.length === 0"
        class="text-gray-500 mt-4 text-center"
    >
      {{ LADEN.sessionLeer }}
    </p>

    <p v-else-if="messages.length === 0" class="text-gray-500 mt-4 text-center">
      {{ LADEN.leer }}
    </p>

    <!-- ✅ System Settings Modal (responsive height) -->
    <div
        v-if="showSystemSettingsModal"
        class="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-6"
        aria-modal="true"
        role="dialog"
    >
      <div class="absolute inset-0 bg-black/40" @click="closeSystemSettingsModal"></div>

      <div
          class="relative w-full max-w-3xl bg-white rounded-xl shadow-xl border border-gray-200
               max-h-[92vh] sm:max-h-[90vh] flex flex-col overflow-hidden"
      >
        <!-- Header -->
        <div class="p-5 border-b border-gray-200 flex items-center justify-between shrink-0">
          <div>
            <h3 class="text-lg font-semibold text-gray-900">Async rating prompts</h3>
            <p class="text-sm text-gray-500 mt-1">
              Update prompt templates and the async rating mode used by the pipeline.
            </p>
          </div>

          <button
              type="button"
              class="px-2 py-1 rounded-md border border-gray-300 bg-white hover:bg-gray-50 text-sm"
              @click="closeSystemSettingsModal"
          >
            Close
          </button>
        </div>

        <!-- Body -->
        <div class="p-5 space-y-5 overflow-y-auto flex-1">
          <!-- Die Platzhalter-Hilfe ist seit dem 18.08. einklappbar. Sie
               ist die längste Passage des Dialogs und stand über allem,
               was man hier tatsächlich bearbeitet. -->
          <div class="text-sm text-gray-600 bg-gray-50 border border-gray-200 rounded-lg leading-relaxed">
            <button
                type="button"
                class="w-full flex items-center gap-2 px-3 py-2 text-left font-semibold text-gray-800
                       rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
                :aria-expanded="promptOffen.platzhalter ? 'true' : 'false'"
                @click="promptBlockUmschalten('platzhalter')"
            >
              <span class="inline-block w-3">{{ promptOffen.platzhalter ? '▾' : '▸' }}</span>
              {{ promptDialog.platzhalter }}
            </button>
            <div v-if="promptOffen.platzhalter" class="px-3 pb-3">
            <ul class="list-disc pl-5 space-y-1">
              <li><code v-text="'{message}'"></code> – content of the currently selected message</li>
              <li><code v-text="'{rag_results}'"></code> – retrieved context (only relevant for RAG prompt)</li>
              <li><code v-text="'{criteria}'"></code> – list of criteria with descriptions</li>
              <li><code v-text="'{metadata}'"></code> – information about sender (Role, Team)</li>
              <li>
                <code v-text="'{referenced_message}'"></code> – initial message, which the current message responds to
                (defaults to empty if message is no response)
              </li>
              <!-- Ohne diesen Eintrag war der Baustein unsichtbar: das aktive
                   Template führt ihn nicht, und der Renderer setzt den Verlauf
                   dann selbst vor {referenced_message}. Wer die Liste hier las,
                   musste schließen, es gebe keinen Verlauf im Prompt — dabei
                   steht er dort seit Paket B2. -->
              <!-- ACHTUNG, hier stand bis zum 18.08. „max. 5“ als feste Zahl.
                   Sie ignorierte rating_context_k und erwähnte die
                   Altersgrenze nicht — beide sind seit dem 17.08. in genau
                   diesem Dialog einstellbar, zwei Blöcke weiter unten. Wer die
                   Liste las, hielt 5 für gesetzt und den Cutoff für nicht
                   vorhanden. -->
              <li>
                <code v-text="'{conversation_context}'"></code> – preceding messages of the conversation
                ({{ kontextPlatzhalterHinweis }})
                <span v-if="kontextEinstellungUngespeichert" class="text-amber-700">
                  — noch nicht gespeichert; bis dahin gilt der Stand in
                  <code>system_settings</code>.
                </span>
              </li>
            </ul>
            <div class="mt-2 text-xs text-gray-500">
              Hinweis: Die genaue Liste der Platzhalter kann je nach Pipeline/Backend variieren.
            </div>
            <!-- Die Sonderstellung des Verlaufs gehört sichtbar dazu: er ist der
                 einzige Block, der auch OHNE seinen Platzhalter im Prompt landet.
                 Wer das nicht weiß, hält den Prompt für vollständig beschrieben
                 durch das, was im Textfeld steht. -->
            <div class="mt-1 text-xs text-gray-500">
              <code v-text="'{conversation_context}'"></code> ist der einzige Baustein, der auch dann gerendert wird,
              wenn der Platzhalter fehlt — er steht dann automatisch vor
              <code v-text="'{referenced_message}'"></code> (ersatzweise vor <code v-text="'{message}'"></code>).
              Wer ihn ausdrücklich setzt, bestimmt die Stelle selbst.
            </div>
            </div>
          </div>

          <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div class="md:col-span-1 space-y-6">
              <!-- Async rating mode -->
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">
                  Async rating mode
                </label>
                <select
                    v-model="systemSettingsForm.async_rating_mode"
                    class="w-full px-3 py-2 border border-gray-300 rounded-md bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-indigo-400"
                >
                  <option value="">(not set)</option>
                  <option value="ML">ML</option>
                  <option value="LLM">LLM</option>
                  <option value="RAG">RAG</option>
                </select>
                <p class="text-xs text-gray-500 mt-2">
                  Controls which pipeline variant is used for rating.
                </p>
              </div>

              <!-- Prüfumfang des Lerngedächtnisses (R4+). Der Schalter
                   existierte seit R4+, war aber nur über eine
                   Umgebungsvariable erreichbar. -->
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">
                  RAG review scope (key: rag_review_scope)
                </label>
                <select
                    v-model="systemSettingsForm.rag_review_scope"
                    class="w-full px-3 py-2 border border-gray-300 rounded-md bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-indigo-400"
                >
                  <option value="">(not set — es gilt approved)</option>
                  <option value="approved">approved</option>
                  <option value="corrected">corrected</option>
                </select>
                <p class="text-xs text-gray-500 mt-2">
                  Welche Nachrichten ins RAG-Lerngedächtnis dürfen.
                  <strong>approved</strong> nimmt jede geprüft markierte Nachricht,
                  <strong>corrected</strong> nur solche mit einer belegten menschlichen
                  Korrektur.
                </p>
                <p class="text-xs text-amber-700 mt-1">
                  Die Verschärfung wirkt <strong>nicht rückwirkend</strong>: der Ingest ist
                  ein Upsert ohne Löschpfad. <code>corrected</code> beschränkt den Zuwachs,
                  räumt aber nichts weg, was unter <code>approved</code> schon drinsteht.
                </p>
              </div>

              <!-- Gesprächskontext der Bewertung (17.08.). Beide Werte waren
                   vorher Modulkonstanten, die beim Import aus der Umgebung
                   gelesen wurden — eine Änderung hieß Container-Neustart.

                   ⚠️ Der Fließtext, der hier bis zum 18.08. stand (fünf Absätze,
                   zwei davon in amber), hängt jetzt an den beiden ⓘ —
                   Entscheidung Simon: der Dialog soll die Einstellung zeigen,
                   nicht sie erklären. Die Sätze selbst sind NICHT gestrichen,
                   sondern nach HILFE_KONTEXT_K / HILFE_KONTEXT_ALTER gewandert;
                   sie tragen die einzigen Auskünfte, die man vor dem Verstellen
                   braucht (was die 0 bedeutet, wann es wirkt, was danach an den
                   Karten anders aussieht).

                   ⚠️ Der Platzhalter nennt den CODE-Default, nicht den
                   geltenden Wert: RATING_CONTEXT_K kann in der Umgebung des
                   Bewertungsprozesses anders stehen, und diese Seite liest sie
                   nicht (der GET liefert ausschliesslich system_settings-Zeilen).
                   Deshalb "Standard: 5" und nicht "5" — die nackte Zahl läse
                   sich als Zusage über den Wert, der gerade gilt. Denselben
                   Vorbehalt trägt kontextPlatzhalterHinweis weiter oben. -->
              <div class="border-t border-gray-200 pt-4">
                <div class="text-sm font-semibold text-gray-800 mb-2">Gesprächskontext</div>

                <div class="space-y-3">
                  <div>
                    <label class="flex items-center gap-1.5 text-sm font-medium text-gray-700 mb-1">
                      Nachrichten im Verlauf k
                      <BaseTooltip :text="HILFE_KONTEXT_K" :oben="false" fixiert>
                        <InformationCircleIcon class="w-4 h-4 text-gray-400 hover:text-gray-600 cursor-help" />
                      </BaseTooltip>
                    </label>
                    <input
                        type="number"
                        min="0"
                        max="50"
                        v-model="systemSettingsForm.rating_context_k"
                        class="w-full px-3 py-2 border border-gray-300 rounded-md bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-indigo-400"
                        placeholder="Standard: 5"
                    />
                  </div>

                  <div>
                    <label class="flex items-center gap-1.5 text-sm font-medium text-gray-700 mb-1">
                      Höchstalter einer Verlaufszeile in Stunden
                      <BaseTooltip :text="HILFE_KONTEXT_ALTER" :oben="false" fixiert>
                        <InformationCircleIcon class="w-4 h-4 text-gray-400 hover:text-gray-600 cursor-help" />
                      </BaseTooltip>
                    </label>
                    <input
                        type="number"
                        min="0"
                        max="8760"
                        v-model="systemSettingsForm.rating_context_max_age_hours"
                        class="w-full px-3 py-2 border border-gray-300 rounded-md bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-indigo-400"
                        placeholder="Standard: 0 (keine Grenze)"
                    />
                  </div>
                </div>
              </div>

              <!-- Worker toggles -->
              <div class="border-t border-gray-200 pt-4">
                <div class="text-sm font-semibold text-gray-800 mb-2">Workers</div>

                <div class="space-y-3 text-sm">
                  <BaseToggle v-model="systemSettingsForm.disable_rating_worker" label="Disable rating worker" />
                  <p class="text-xs text-gray-500 -mt-2">
                    If enabled, the backend rating worker is disabled and n8n takes over processing.
                  </p>

                  <BaseToggle v-model="systemSettingsForm.disable_comment_worker" label="Disable comment worker" />
                  <p class="text-xs text-gray-500 -mt-2">
                    If enabled, the backend comment worker is disabled and n8n takes over processing.
                  </p>

                  <BaseToggle v-model="systemSettingsForm.disable_rag_worker" label="Disable RAG worker" />
                  <p class="text-xs text-gray-500 -mt-2">
                    If enabled, the backend RAG worker is disabled and n8n takes over processing.
                  </p>
                </div>
              </div>
            </div>

            <div class="md:col-span-2">
              <!-- Die Prompts, jeder für sich auf- und zuklappbar (18.08.).
                   ⚠️ Der wirksame ist markiert und steht offen. Bis zum
                   18.08. lagen hier drei Textfelder untereinander, ohne
                   jede Auskunft darüber, welches davon gerade bewertet —
                   die Angabe gab es nur auf der Versionsseite, und wer hier
                   den falschen bearbeitete, merkte es an nichts.
                   Aufbau nach dem Hausmuster der PromptVersionsPage:
                   Knopf + aria-expanded + Chevron + v-if. -->
              <p v-if="!wirksamerPrompt && !wirksamFehler" class="text-xs text-gray-500 mb-2">
                {{ promptDialog.unbekannt }}
              </p>
              <p v-else-if="wirksamFehler" class="text-xs text-amber-700 mb-2">
                {{ wirksamFehler }}
              </p>
              <p v-else-if="!wirksamerPrompt.prompt_key" class="text-xs text-gray-500 mb-2">
                {{ promptDialog.keinPrompt }}
              </p>

              <div class="space-y-3">
                <div
                    v-for="b in promptBloecke"
                    :key="b.schluessel"
                    class="border rounded-lg"
                    :class="istAktiverPrompt(b)
                      ? 'border-indigo-400 ring-1 ring-indigo-200'
                      : 'border-gray-200'"
                >
                  <button
                      type="button"
                      class="w-full flex items-center gap-2 px-3 py-2 text-left text-sm
                             font-medium text-gray-700 rounded-lg
                             focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
                      :aria-expanded="promptOffen[b.schluessel] ? 'true' : 'false'"
                      @click="promptBlockUmschalten(b.schluessel)"
                  >
                    <span class="inline-block w-3">{{ promptOffen[b.schluessel] ? '▾' : '▸' }}</span>
                    <span>{{ b.titel }}</span>
                    <!-- ⚠️ Der Schlüssel bleibt sichtbar: er ist der Name,
                         unter dem der Text in system_settings steht, und
                         die Verbindung zu Logzeilen und Versionsseite. -->
                    <code class="text-xs text-gray-400">{{ b.schluessel }}</code>
                    <span
                        v-if="istAktiverPrompt(b)"
                        class="ml-auto shrink-0 px-1.5 py-0.5 rounded text-xs
                               bg-indigo-50 text-indigo-700 border border-indigo-200"
                    >
                      {{ promptDialog.aktiv }}
                    </span>
                    <span
                        v-else-if="!b.gegated"
                        class="ml-auto shrink-0 text-xs text-gray-400"
                    >
                      {{ promptDialog.ohneGate }}
                    </span>
                  </button>

                  <div v-if="promptOffen[b.schluessel]" class="px-3 pb-3">
                    <p class="text-xs text-gray-500 mb-1">{{ b.hinweis }}</p>
                    <!--
                      NUR der Ausfall wird hingeschrieben: gilt die Vorgabe,
                      steht sie als Text im Feld und erklaert sich selbst.
                      Laesst sie sich dagegen nicht laden, KANN das Feld leer
                      sein -- und leer liest sich als "kein Prompt".
                    -->
                    <p
                        v-if="b.hatVorgabe && vorlageFehler"
                        class="text-xs text-amber-700 mb-1"
                    >
                      {{ promptDialog.vorgabeFehlt }} ({{ vorlageFehler }})
                    </p>
                    <textarea
                        v-model="systemSettingsForm[b.schluessel]"
                        rows="8"
                        class="w-full px-3 py-2 border border-gray-300 rounded-md bg-white
                               text-gray-900 focus:outline-none focus:ring-2
                               focus:ring-indigo-400 font-mono text-sm"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div v-if="systemSettingsError" class="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md p-3">
            {{ systemSettingsError }}
          </div>

          <div
              v-if="systemSettingsSavedMessage"
              class="text-sm rounded-md p-3 border"
              :class="showPromptVersionsHint
                ? 'text-amber-800 bg-amber-50 border-amber-200'
                : 'text-green-700 bg-green-50 border-green-200'"
          >
            {{ systemSettingsSavedMessage }}
            <router-link
                v-if="showPromptVersionsHint"
                to="/async/prompt-versions"
                class="underline font-medium ml-1"
            >
              Zu den Prompt-Versionen
            </router-link>
          </div>
        </div>

        <!-- Footer -->
        <div class="p-5 border-t border-gray-200 flex items-center justify-between shrink-0">
          <div class="text-sm text-gray-500">
            <span v-if="systemSettingsLoading">Loading settings…</span>
            <span v-else-if="systemSettingsSaving">Saving…</span>
            <span v-else-if="systemSettingsDirty" class="text-amber-700">
              You have unsaved changes.
            </span>
            <span v-else> </span>
          </div>

          <div class="flex items-center gap-2">
            <button
                type="button"
                class="px-3 py-2 rounded-md border border-gray-300 bg-white hover:bg-gray-50 text-sm"
                :disabled="systemSettingsSaving || systemSettingsLoading"
                @click="reloadSystemSettings"
            >
              Reload
            </button>

            <button
                type="button"
                class="px-3 py-2 rounded-md bg-indigo-600 text-white hover:bg-indigo-700 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                :disabled="systemSettingsSaving || systemSettingsLoading || !systemSettingsDirty"
                @click="saveSystemSettings"
            >
              {{ systemSettingsSaving ? "Saving..." : "Save" }}
            </button>
          </div>
        </div>
      </div>
    </div>
    <!-- /Modal -->

    <!-- Genau EIN Bestaetigungsdialog fuer die ganze Seite, gespeist aus
         confirmState.
         ACHTUNG, die Position ist nicht beliebig: er MUSS nach dem Prompt-Modal
         stehen. Beide liegen auf z-50, und bei gleichem z-index entscheidet die
         Reihenfolge im DOM -- weiter oben stehend waere er von ihm verdeckt.
         Und er gehoert nach MessagesList, nicht in MessageCard: eine im
         Fokus-Modus abgedunkelte Karte erzeugt ueber opacity einen Stacking
         Context, aus dem ein Dialog nicht herauskaeme. -->
    <ConfirmModal
        :isOpen="confirmState.open"
        :title="confirmState.title"
        :message="confirmState.message"
        :confirmLabel="confirmState.confirmLabel"
        :confirmVariant="confirmState.confirmVariant"
        :cancelLabel="confirmState.cancelLabel"
        :extraLabel="confirmState.extraLabel"
        :extraVariant="confirmState.extraVariant"
        @confirm="closeConfirm('confirm')"
        @extra="closeConfirm('extra')"
        @cancel="closeConfirm('cancel')"
    />
  </div>
</template>

<script>
import BaseToggle from "@/BaseComponents/BaseToggle.vue";
import BaseSelect from "@/BaseComponents/BaseSelect.vue";
import BaseButton from "@/BaseComponents/BaseButton.vue";
import ConfirmModal from "@/BaseComponents/ConfirmModal.vue";
import BaseTooltip from "@/BaseComponents/BaseTooltip.vue";
import { InformationCircleIcon } from "@heroicons/vue/24/outline";
import MessageCard from "../components/MessageComponents/MessageCard.vue";
// Der erste Importeur dieses Moduls -- es lag seit jeher ungenutzt da.
import { usePolling } from "../usePolling";
import { LADEN, TREFFER, ladeFehlerGrund } from "../laden";
import { DISCORD_SERVER } from "../discordServer";
import { ERKLAERUNG } from "../erklaerungen";
import BaseInfoIcon from "@/BaseComponents/BaseInfoIcon.vue";
import { getAndereServer } from "@/live-sessions/api/session_category";
import {
  listMessages,
  getRatingStatus,
  getRagStatus,
  approveMessage,
  setMessageCriterionRating,
  deleteMessage,
  rateAgainMessage,
} from "../api/messages";
import { getCriterias } from "@/api/criterias";
import { getSessions, updateSession } from "@/live-sessions/api/sessions";
import { triggerMessageBotInitialSync } from "../api/bot";
import {
  getContextRelevanceDefault,
  getSystemSettings,
  updateSystemSettings,
} from "../api/systemSettings";
import { SHORTCUT_GRUPPEN, SKALA_TEXT } from "../shortcuts";
import { DIALOG, PROMPT_BLOECKE, istWirksam } from "../promptDialog";
import { getEffectivePromptKey } from "../api/promptVersions";
// Der Schluessel und das Format stehen NUR im Modul -- diese Seite kennt beide
// nicht. Sonst haette die Nachrichtenliste eine eigene Vorstellung davon, was
// eine Merkliste ist, und die Reflexionsseite eine zweite.
import {
  lesen as merklisteLesen,
  abonnieren as merklisteAbonnieren,
  alleVergessen as merklisteAlleVergessen,
} from "../merkliste";

// Diese beiden Schlüssel gehen seit R3 nicht mehr direkt nach system_settings,
// sondern werden zu einer Entwurfsversion in prompt_versions. Sie werden erst
// wirksam, wenn ein Regressionsvergleich bestanden ist und jemand die Version
// aktiviert. comment_prompt_template gehört NICHT dazu -- der bleibt
// unversioniert und wird direkt gespeichert.
// Die beiden Hilfetexte des Gespraechskontexts (18.08.).
//
// Sie standen als fuenf Absaetze im Dialog, wanderten in die Tooltips und sind
// dort noch einmal gekuerzt worden (Simon): der Dialog soll die Einstellung
// zeigen. Gestrichen ist alles, was nicht VOR dem Verstellen gebraucht wird --
// was ohne Eintrag gilt (steht als Standard im Platzhalter) und wie man eine
// Einstellung wieder loswird (steht in der Konfigurationsdokumentation).
//
// Geblieben sind drei Auskuenfte, und jede davon hat einen Fehler verhindert:
// die Bedeutung der 0, die zweigeteilte Wirkung (Bewertung spaeter, Anzeige
// sofort) und der Vorbehalt der Altersgrenze.
const HILFE_KONTEXT_K =
  "Wie viele Vorgängernachrichten in den Bewertungsprompt gehen. " +
  "0 heißt kein Verlauf, nicht „unbegrenzt“.\n\n" +
  "Auf die Bewertung wirkt es erst beim nächsten Bewertungslauf; bestehende " +
  "werden nicht neu gerechnet. Die Verlaufsanzeige der Karten ändert sich " +
  "sofort — ältere Nachrichten melden danach eine Abweichung im Belegstand.\n\n" +
  "Gilt auch für den Optimierer.";

const HILFE_KONTEXT_ALTER =
  "Eine Verlaufszeile, die mehr als so viele Stunden vor der bewerteten " +
  "Nachricht liegt, fällt heraus — gemessen wird gegen die Nachricht, nicht " +
  "gegen jetzt. 0 heißt keine Grenze.\n\n" +
  "Betrifft nur Zeilen aus dem Kanalfenster; eine Antwortkette ist ein Beleg " +
  "und wird nicht gekürzt.";

const PROMPT_VERSION_KEYS = ["llm_prompt_template", "rag_prompt_template"];

export default {
  components: {
    BaseToggle, BaseSelect, BaseButton, ConfirmModal, MessageCard,
    BaseTooltip, InformationCircleIcon, BaseInfoIcon,
  },

  data() {
    // Die Warteschlange, wie der RatingWorker sie sieht.
    //
    // ⚠️ null heißt „noch nicht geladen ODER nicht erreichbar“ — und das
    // ist NICHT dasselbe wie „niemand wartet“. Ohne die Unterscheidung
    // behauptete die Liste beim ersten Rendern, alles sei fertig.
    // Die Zahlen kommen vollständig vom Dienst; hier wird nichts
    // gerechnet — insbesondere nicht der Nenner, denn der ist global und
    // nicht die Kriterienzahl der gewählten Session.
    return {
      ratingStatus: null,
      // Der Zwilling für die Vektorisierung. `null` heißt „unbekannt"; die
      // Karte fällt dann auf message.vectorized zurück.
      ragStatus: null,
      // Aus shortcuts.js, damit die Uebersicht im Popover und die Tooltips an
      // den Bedienelementen dieselbe Quelle haben.
      shortcutGruppen: SHORTCUT_GRUPPEN,
      skalaText: SKALA_TEXT,

      // Fliesstext, nur durchgereicht. Ohne diese Zeilen ist :text im
      // Template undefined -- BaseTooltip rendert dann per v-if gar
      // nichts, und zwar lautlos.
      HILFE_KONTEXT_K,
      HILFE_KONTEXT_ALTER,

      // Nur die ANZAHL. Welche Nachrichten gemerkt sind, weiss jede Karte
      // selbst; die Liste braucht davon nur den Zaehler fuer den Streifen, und
      // eine zweite Kopie der ids hier liefe der ersten hinterher.
      merkAnzahl: 0,
      merkAbbestellen: null,

      messages: [],
      hideReviewed: false,
      loading: false,
      // Der Grund des letzten gescheiterten Ladevorgangs, "" wenn es keinen
      // lesbaren gibt, null wenn nichts gescheitert ist.
      //
      // ⚠️ null und "" sind NICHT dasselbe: null heißt „nicht gescheitert",
      // "" heißt „gescheitert, aber ohne belastbaren Grund". Die beiden
      // zusammenzuwerfen hieße, einen Fehlschlag ohne Meldung stillschweigend
      // in einen Erfolg zu verwandeln — genau der Zustand, aus dem heraus die
      // Seite bisher „Keine Nachrichten gefunden." behauptete.
      ladeFehler: null,
      triggeringBot: false,
      botTriggerMessage: "",

      // Die Sätze zum Ladezustand stehen in laden.js — durchgereicht, weil
      // Vue aus methods nur Funktionen bindet und ein Konstanten-Objekt dort
      // zur Laufzeit undefined wäre, ohne Konsolenmeldung.
      LADEN,
      TREFFER,
      DISCORD_SERVER,
      ERKLAERUNG,
      // GET /session-categories/andere-server fuer die gewaehlte Session
      // (01.10.): aktiver Server und Nachrichten derselben Kategorien auf
      // anderen Servern. null, solange nicht geladen oder keine Session.
      andere: null,

      lastUpdate: null,
      criterias: [],

      sessions: [],
      selectedSessionId: "",
      sessionCriterias: [],

      filterAuthorId: "",
      filterTeamId: "",
      filterChannel: "",
      filterCategory: "",
      filterReviewState: "",

      // Hinweiskanal fuer alles, was frueher ein alert() war.
      noticeText: "",
      noticeTone: "info",
      noticeTimer: null,

      // Der eine Bestaetigungsdialog der Seite. confirmResolve ist bewusst
      // nicht reaktiv gedacht, steht aber hier, damit ESLint die Eigenschaft
      // kennt.
      confirmState: {
        open: false,
        title: "",
        message: "",
        confirmLabel: "",
        confirmVariant: "delete",
        cancelLabel: "Abbrechen",
        extraLabel: "",
        extraVariant: "neutral",
      },
      confirmResolve: null,

      // Welche Nachricht gerade einen offenen Kriterien-Editor hat. Treibt den
      // Fokus-Modus; die Karten melden es selbst.
      editorOpenForId: null,

      activeMessageId: null,
      messageRefs: {},

      criteriaWindowStart: 0,
      criteriaWindowSize: 10,

      isRestoring: false,
      pendingFilters: {
        hideReviewed: null,
        selectedSessionId: null,
        filterAuthorId: null,
        filterTeamId: null,
        filterChannel: null,
        filterCategory: null,
        filterReviewState: null,
      },

      showSystemSettingsModal: false,
      systemSettingsLoading: false,
      systemSettingsSaving: false,
      systemSettingsError: "",
      systemSettingsSavedMessage: "",
      // Bleibt stehen, bis das Modal geschlossen wird -- die Meldung zu einem
      // Entwurf laeuft bewusst nicht nach 2,5 Sekunden weg wie ein "Saved".
      showPromptVersionsHint: false,

      // Die Bloecke des Prompt-Dialogs und seine Saetze -- aus
      // promptDialog.js durchgereicht.
      // ACHTUNG: was data() nicht durchreicht, ist im Template
      // undefined, und zwar STILL. Der Dialog zeigte dann leere
      // Ueberschriften statt einer Fehlermeldung.
      promptBloecke: PROMPT_BLOECKE,
      promptDialog: DIALOG,

      // Welcher Block offen ist, nach Schluessel. Der wirksame Prompt
      // wird beim Oeffnen aufgeklappt -- aber erst, wenn feststeht,
      // welcher das IST.
      promptOffen: {},

      // Die Antwort von /prompt-versions/effective-key: welcher Modus
      // gilt und welcher Prompt-Schluessel damit bewertet.
      // ACHTUNG, null heisst 'noch nicht bekannt' und NICHT 'keiner' --
      // solange sie null ist, wird kein Block markiert.
      wirksamerPrompt: null,
      wirksamFehler: null,

      // Der Grund, wenn die geltende Vorlage NICHT geladen werden konnte.
      // ⚠️ Leer heißt "kein Ausfall", nicht "noch nichts versucht" -- der
      // Satz darf nur stehen, wenn wirklich etwas fehlt.
      vorlageFehler: "",

      systemSettingsForm: {
        llm_prompt_template: "",
        rag_prompt_template: "",
        comment_prompt_template: "",
        context_relevance_prompt_template: "",
        async_rating_mode: "",
        rag_review_scope: "",

        // Leerer String = "nicht gesetzt", NICHT 0. Die 0 ist bei beiden ein
        // gültiger Wert (kein Verlauf bzw. keine Altersgrenze) und muss sich
        // von "nichts eingestellt" unterscheiden lassen.
        rating_context_k: "",
        rating_context_max_age_hours: "",

        disable_rating_worker: false,
        disable_comment_worker: false,
        disable_rag_worker: false,
      },
      systemSettingsInitial: null,

      showInfoPopover: false,

      // ✅ End session state
      isEndingSession: false,
    };
  },

  computed: {
    formattedLastUpdate() {
      if (!this.lastUpdate) return null;
      const d = new Date(this.lastUpdate);
      if (Number.isNaN(d.getTime())) return this.lastUpdate;
      return d.toLocaleString();
    },

    systemSettingsDirty() {
      if (!this.systemSettingsInitial) return false;
      return JSON.stringify(this.systemSettingsForm) !== JSON.stringify(this.systemSettingsInitial);
    },

    /**
     * Was der Platzhalter {conversation_context} tatsächlich liefert.
     *
     * ⚠️ Hier stand bis zum 18.08. die fest verdrahtete Zahl „max. 5“. Sie
     * ignorierte rating_context_k vollständig und erwähnte die Altersgrenze
     * gar nicht — wer die Liste las, hielt 5 für gesetzt und den Cutoff für
     * nicht vorhanden. Die 5 ist die Modulkonstante von
     * conversation_context.py; wirksam ist die Einstellung, ersatzweise die
     * ENV des jeweiligen Prozesses.
     *
     * ⚠️ Das Frontend darf keine ENV-Vorgabe raten. Der GET liefert
     * ausschließlich system_settings-Zeilen; ist dort nichts gesetzt, macht
     * _zahlAusDb den leeren String daraus, und die geltende Zahl steht in der
     * Umgebung des BEWERTUNGSPROZESSES — die kennt diese Seite nicht und darf
     * sie deshalb auch nicht nennen.
     */
    kontextPlatzhalterHinweis() {
      // Solange nichts geladen ist, sagt der Text keine Zahlen. Beim Öffnen
      // steht showSystemSettingsModal auf true, BEVOR reloadSystemSettings
      // durch ist — das Formular führt dann noch seine Vorgabewerte.
      if (this.systemSettingsLoading || !this.systemSettingsInitial) {
        return "reply chain and/or same channel, oldest first — "
            + "Umfang und Altersgrenze werden geladen …";
      }

      const k = this.systemSettingsForm.rating_context_k;
      const alter = this.systemSettingsForm.rating_context_max_age_hours;
      const teile = ["reply chain and/or same channel, oldest first"];

      teile.push(k === "" || k === null || k === undefined
          ? "höchstens so viele Zeilen, wie RATING_CONTEXT_K im Bewertungsprozess "
            + "vorgibt (hier nicht ablesbar)"
          : `höchstens ${Number(k)} Zeilen (Einstellung rating_context_k)`);

      if (alter === "" || alter === null || alter === undefined) {
        teile.push("Altersgrenze aus RATING_CONTEXT_MAX_AGE_HOURS (hier nicht ablesbar)");
      } else if (Number(alter) > 0) {
        // Derselbe Vorbehalt wie im Tooltip der Karte: geschnitten werden nur
        // Annahmen aus zeitlicher Nähe, keine belegten Kettenglieder.
        teile.push(`nichts älter als ${Number(alter)} h vor der bewerteten Nachricht `
            + "— nur Zeilen aus dem Kanalfenster, eine Antwortkette wird nicht gekürzt");
      } else {
        teile.push("ohne Altersgrenze");
      }

      return teile.join(", ");
    },

    /**
     * Ob die zwei Kontextfelder vom gespeicherten Stand abweichen.
     *
     * ⚠️ Bewusst NICHT systemSettingsDirty: das vergleicht das ganze Formular
     * einschließlich der drei Prompt-Textareas. Wer nur einen Prompt anfasst,
     * bekäme sonst den Hinweis, seine Kontexteinstellung sei ungespeichert.
     */
    kontextEinstellungUngespeichert() {
      const ausgang = this.systemSettingsInitial;
      if (!ausgang) return false;
      return ["rating_context_k", "rating_context_max_age_hours"].some((feld) => {
        const jetzt = this.systemSettingsForm[feld];
        const vorher = ausgang[feld];
        // Über String verglichen, damit "" (nicht gesetzt) und 0 (gültiger
        // Wert) sich unterscheiden — Number("") ist 0, und genau daran wäre
        // die Unterscheidung gescheitert, um die es bei beiden Feldern geht.
        return String(jetzt ?? "") !== String(vorher ?? "");
      });
    },

    sessionOptions() {
      const flattenSessions = (sessions, depth = 0) =>
          (sessions || []).flatMap((s) => [
            { value: s.id.toString(), label: `${"— ".repeat(depth)}${s.title}` },
            ...(s.children ? flattenSessions(s.children, depth + 1) : []),
          ]);

      return [{ value: "", label: "Alle Sessions" }, ...flattenSessions(this.sessions)];
    },

    selectedSessionObj() {
      const id = this.selectedSessionId ? Number(this.selectedSessionId) : null;
      if (!id) return null;

      const flatten = (sessions) =>
          (sessions || []).flatMap((s) => [s, ...(s.children ? flatten(s.children) : [])]);

      const all = flatten(this.sessions);
      return all.find((s) => s.id === id) || null;
    },

    selectedSessionEnded() {
      return !!this.selectedSessionObj?.ended;
    },

    // Der Tooltip an der Umfangszeile: Zahl je anderem Server, dann der
    // Hinweis, dass sie bei erneuter Wahl wieder sichtbar sind. Beide Saetze
    // aus den Modulen -- hier wird nur zusammengesetzt.
    // Die Umfangszeile nur fuer eine Session MIT Credential: ohne Credential
    // gibt es weder einen Server noch eigene Teilnehmer, und die Session
    // zeigt nichts -- jeder Satz ueber ihren Umfang waere falsch.
    umfangZeigen() {
      return !!(this.selectedSessionId && this.andere && this.selectedSessionObj?.micro_credential_id);
    },

    andereServerText() {
      const teile = (this.andere?.andere || []).map((a) => ({
        name: a.guild_name || a.guild_id,
        nachrichten: a.nachrichten,
      }));
      return `${TREFFER.andereServer(teile)}\n\n${ERKLAERUNG.wiederSichtbar}`;
    },

    authorOptions() {
      const map = new Map();
      for (const m of this.messages || []) {
        const u = m.user;
        if (!u) continue;
        const label =
            `${u.first_name || ""} ${u.last_name || ""}`.trim() ||
            u.username ||
            `User #${u.id}`;
        if (!map.has(u.id)) map.set(u.id, { value: String(u.id), label });
      }
      return [
        { value: "", label: "Alle Verfasser" },
        ...Array.from(map.values()).sort((a, b) => a.label.localeCompare(b.label, "en")),
      ];
    },

    teamOptions() {
      const map = new Map();
      for (const m of this.messages || []) {
        const t = m.user && m.user.team;
        if (!t) continue;
        if (!map.has(t.id)) {
          map.set(t.id, { value: String(t.id), label: t.name || `Team #${t.id}` });
        }
      }
      return [
        { value: "", label: "Alle Teams" },
        ...Array.from(map.values()).sort((a, b) => a.label.localeCompare(b.label, "en")),
      ];
    },

    channelOptions() {
      const base = this.getMessagesFilteredForOptions({ ignoreChannel: true });
      const set = new Set();
      for (const m of base) if (m.channel) set.add(m.channel);
      const opts = Array.from(set).map((ch) => ({ value: ch, label: ch }));
      return [
        { value: "", label: "Alle Kanäle" },
        ...opts.sort((a, b) => a.label.localeCompare(b.label, "en")),
      ];
    },

    categoryOptions() {
      const base = this.getMessagesFilteredForOptions({ ignoreCategory: true });
      const set = new Set();
      for (const m of base) if (m.category) set.add(m.category);
      const opts = Array.from(set).map((c) => ({ value: c, label: c }));
      return [
        { value: "", label: "Alle Kategorien" },
        ...opts.sort((a, b) => a.label.localeCompare(b.label, "en")),
      ];
    },

    reviewStateOptions() {
      return [
        { value: "", label: "Alle Bewertungen" },
        { value: "corrected", label: "Nur korrigierte" },
        { value: "untouched", label: "Noch nicht korrigiert" },
      ];
    },

    filteredByCriteria() {
      const authorId = this.filterAuthorId ? Number(this.filterAuthorId) : null;
      const teamId = this.filterTeamId ? Number(this.filterTeamId) : null;
      const channel = this.filterChannel || null;
      const category = this.filterCategory || null;

      return (this.messages || []).filter((m) => {
        if (authorId && (!m.user || m.user.id !== authorId)) return false;
        if (teamId && (!m.user || !m.user.team || m.user.team.id !== teamId)) return false;
        if (channel && m.channel !== channel) return false;
        if (category && m.category !== category) return false;
        return true;
      });
    },

    progressTotal() {
      return this.filteredByCriteria.length;
    },
    progressReviewed() {
      return this.filteredByCriteria.filter((m) => m.reviewed).length;
    },
    progressPercent() {
      const total = this.progressTotal;
      if (!total) return 0;
      return Math.round((this.progressReviewed / total) * 100);
    },

    zellStatistik() {
      // Zaehlt ZELLEN, nicht Nachrichten. Der Fortschrittsbalken darueber
      // zaehlt Nachrichten -- eine durchgewinkte zaehlt dort genauso wie eine
      // mit zwoelf Korrekturen.
      //
      // ACHTUNG, die Falle: der RatingWorker stempelt score_source='model' und
      // laesst model_value LEER; gefuellt wird es erst beim Korrigieren. Wer
      // "Modell != Mensch" ueber count_value !== model_value rechnet, OHNE
      // vorher auf 'human' zu pruefen, zaehlt jede unkorrigierte Zelle als
      // Vergleich gegen null -- und bekommt eine Zahl, die nichts bedeutet.
      const erlaubt = this.selectedSessionId
          ? new Set((this.effectiveCriterias || []).map((c) => c.criterion?.id ?? c.id))
          : null;

      let zellen = 0;
      let human = 0;
      let abweichend = 0;
      let humanOhneModell = 0;

      for (const m of this.filteredByCriteria) {
        for (const mc of m.criteria_ratings || []) {
          const cid = mc.criterion?.id ?? mc.criterion_id;
          // Eine Nachricht traegt auch Bewertungen zu Kriterien ausserhalb der
          // gewaehlten Session. Ohne den Filter zaehlte die Leiste Zellen mit,
          // die auf der Seite gar nicht sichtbar sind.
          if (erlaubt && !erlaubt.has(cid)) continue;
          zellen += 1;
          if (mc.score_source !== "human") continue;
          human += 1;
          if (mc.model_value === null || mc.model_value === undefined) {
            humanOhneModell += 1;
            continue;
          }
          if (mc.count_value !== mc.model_value) abweichend += 1;
        }
      }

      return { zellen, human, abweichend, humanOhneModell };
    },

    visibleMessages() {
      const base = this.filteredByCriteria;
      let list = this.hideReviewed ? base.filter((m) => !m.reviewed) : base;

      // Der Zustandsfilter greift HIER und nicht in filteredByCriteria. Sonst
      // waere die Kennzahlleiste selbstbezueglich: auf "corrected" filtern und
      // "100 % corrected" ablesen. hideReviewed ist aus demselben Grund
      // ebenfalls hier getrennt.
      if (this.filterReviewState) {
        list = list.filter((m) => this.passtZumZustand(m));
      }

      const toTs = (m) => {
        const d = new Date(m?.sent_at ?? m?.created_at ?? 0);
        const ts = d.getTime();
        return Number.isNaN(ts) ? 0 : ts;
      };

      return [...list].sort((a, b) => toTs(a) - toTs(b));
    },

    reviewedCount() {
      return this.filteredByCriteria.filter((m) => m.reviewed).length;
    },
    unreviewedCount() {
      return this.filteredByCriteria.filter((m) => !m.reviewed).length;
    },

    effectiveCriterias() {
      if (!this.selectedSessionId) return [];
      return this.sessionCriterias || [];
    },

    windowedCriterias() {
      const all = this.effectiveCriterias || [];
      if (!all.length) return [];
      const start = Math.min(this.criteriaWindowStart, Math.max(0, all.length - 1));
      const end = Math.min(start + this.criteriaWindowSize, all.length);
      return all.slice(start, end);
    },

    totalWindows() {
      const len = this.effectiveCriterias.length || 0;
      if (!len) return 1;
      return Math.ceil(len / this.criteriaWindowSize);
    },
    currentWindowIndex() {
      const len = this.effectiveCriterias.length || 0;
      if (!len) return 1;
      return Math.floor(this.criteriaWindowStart / this.criteriaWindowSize) + 1;
    },
    canPagePrev() {
      return this.criteriaWindowStart > 0;
    },
    canPageNext() {
      const len = this.effectiveCriterias.length || 0;
      return this.criteriaWindowStart + this.criteriaWindowSize < len;
    },
  },

  watch: {
    hideReviewed(val) {
      if (this.isRestoring) return;
      try {
        localStorage.setItem("async_messages_hideReviewed", val ? "1" : "0");
      } catch (e) {
        console.warn("Failed to persist hideReviewed state", e);
      }
    },

    filterAuthorId(val) {
      if (this.isRestoring) return;
      try {
        localStorage.setItem("async_messages_filterAuthorId", this.normalizeSelectValue(val));
      } catch (e) {
        console.warn("Failed to persist filterAuthorId", e);
      }
    },
    filterTeamId(val) {
      if (this.isRestoring) return;
      try {
        localStorage.setItem("async_messages_filterTeamId", this.normalizeSelectValue(val));
      } catch (e) {
        console.warn("Failed to persist filterTeamId", e);
      }
    },

    filterChannel(val) {
      if (this.isRestoring) return;
      try {
        localStorage.setItem("async_messages_filterChannel", this.normalizeSelectValue(val));
      } catch (e) {
        console.warn("Failed to persist filterChannel", e);
      }
    },

    filterReviewState(val) {
      if (this.isRestoring) return;
      try {
        localStorage.setItem("async_messages_filterReviewState", this.normalizeSelectValue(val));
      } catch (e) {
        console.warn("Failed to persist filterReviewState", e);
      }
    },

    filterCategory(val) {
      if (this.isRestoring) return;
      try {
        localStorage.setItem("async_messages_filterCategory", this.normalizeSelectValue(val));
      } catch (e) {
        console.warn("Failed to persist filterCategory", e);
      }

      const ch = this.filterChannel || "";
      if (ch) {
        const stillValid = (this.channelOptions || []).some((o) => String(o.value) === String(ch));
        if (!stillValid) {
          const prevCh = ch;
          this.filterChannel = "";

          const catLabel = this.filterCategory || "All categories";
          this.showFilterHint(
              `Channel "${prevCh}" is not available for category "${catLabel}". Channel filter was reset to "All channels".`
          );
        }
      }
    },

    selectedSessionId(val) {
      if (this.isRestoring) return;
      try {
        localStorage.setItem("async_messages_selectedSessionId", this.normalizeSelectValue(val));
      } catch (e) {
        console.warn("Failed to persist selectedSessionId", e);
      }
      this.fetchCriteriasBySession();
      // Seit die Session serverseitig filtert, reicht das Nachziehen der
      // Kriterienspalten nicht mehr -- die LISTE selbst ist eine andere.
      this.fetchMessages();
      this.ladeAndereServer();
      this.criteriaWindowStart = 0;
    },

    visibleMessages: {
      immediate: true,
      handler(newList) {
        // Zweite Sicherung gegen einen haengenden Fokus-Modus. Die erste ist
        // das beforeUnmount-Emit in MessageCard; faellt eine Karte MIT offenem
        // Editor aus der Liste, waere die Seite sonst dauerhaft abgedunkelt.
        if (
            this.editorOpenForId !== null &&
            !(newList || []).some((m) => m.id === this.editorOpenForId)
        ) {
          this.editorOpenForId = null;
        }

        if (!newList || newList.length === 0) {
          this.activeMessageId = null;
          return;
        }
        if (!newList.some((m) => m.id === this.activeMessageId)) {
          this.activeMessageId = newList[0].id;
        }
        this.$nextTick(() => this.scrollActiveIntoView());
      },
    },

    effectiveCriterias() {
      this.criteriaWindowStart = 0;
    },
  },

  methods: {
    /**
     * Die Merkliste leeren.
     *
     * Ohne Rückfrage, und das ist eine Entscheidung: gemerkt wird mit einem
     * Klick, vergessen wird mit einem Klick. Ein Bestätigungsdialog für das
     * Löschen einer Liste von IDs, die in zehn Sekunden wieder zusammengeklickt
     * ist, wäre Zeremonie — anders als beim Einfrieren, das unumkehrbar ist.
     */
    merklisteLeeren() {
      merklisteAlleVergessen();
      this.merkAnzahl = 0;
    },

    _toBoolDb(val) {
      if (val === true) return true;
      if (val === false) return false;
      if (val == null) return false;
      const s = String(val).trim().toLowerCase();
      return s === "1" || s === "true" || s === "yes" || s === "y";
    },

    _toDb01(val) {
      return val ? "1" : "0";
    },

    /**
     * Ein Zahlenwert aus system_settings für ein Eingabefeld.
     *
     * Leer bleibt leer ("nicht gesetzt"), alles andere kommt als String
     * zurück — genau so, wie ein <input type="number"> ihn auch liefert.
     * Dass beide Seiten dieselbe Darstellung haben, ist die Voraussetzung
     * dafür, dass der Dirty-Vergleich (JSON.stringify über das ganze
     * Formular) nicht dauerhaft anschlägt.
     */
    _zahlAusDb(wert) {
      if (wert === null || wert === undefined) return "";
      const roh = String(wert).trim();
      return roh === "" ? "" : roh;
    },

    /**
     * Die beiden Kontextfelder in die Nutzlast, wenn sie gesetzt und geändert
     * sind.
     *
     * ⚠️ Hier steht bewusst NICHT das Muster von rag_review_scope
     * (`if (this.systemSettingsForm.x && ...)`). Bei diesen beiden Feldern ist
     * **0 ein gültiger Wert** — k=0 heißt kein Verlauf, Höchstalter 0 heißt
     * keine Grenze. Über den Wahrheitswert geprüft ließe sich ausgerechnet
     * die 0 nie speichern, also genau die Einstellung, mit der man die
     * Funktion abschaltet. Geprüft wird deshalb gegen den leeren String.
     *
     * Verglichen wird über Number() auf beiden Seiten: `<input type="number">`
     * liefert einen String, der Ausgangswert kommt ebenfalls als String aus
     * der Datenbank — ein `"5" !== 5` erzeugte sonst einen Dauer-Dirty-Zustand
     * oder verschluckte eine echte Änderung.
     */
    _ergaenzeKontextfelder(payload, ausgang = null) {
      const felder = ["rating_context_k", "rating_context_max_age_hours"];
      for (const feld of felder) {
        const wert = this.systemSettingsForm[feld];
        if (wert === "" || wert === null || wert === undefined) continue;

        const zahl = Number(wert);
        if (!Number.isFinite(zahl)) continue;

        if (ausgang) {
          const vorher = ausgang[feld];
          const unveraendert =
              vorher !== "" && vorher !== null && vorher !== undefined &&
              Number(vorher) === zahl;
          if (unveraendert) continue;
        }
        payload[feld] = zahl;
      }
    },

    toggleInfoPopover() {
      this.showInfoPopover = !this.showInfoPopover;
    },
    closeInfoPopover() {
      this.showInfoPopover = false;
    },
    handleClickOutside(e) {
      if (!this.showInfoPopover) return;
      const root = this.$refs.infoPopoverRoot;
      if (!root) return;
      if (root.contains(e.target)) return;
      this.closeInfoPopover();
    },

    /**
     * Ein Klick neben die bearbeitete Karte beendet den Fokus-Modus.
     *
     * Warum in der CAPTURE-Phase und nicht als @click auf dem Container: der
     * Handler muss VOR dem @click der Karte laufen. Nur so kann er einen Klick,
     * der ungespeicherte Änderungen wegdrängen würde, noch abfangen, bevor die
     * geklickte Karte sich zur aktiven macht.
     *
     * Der Klick tut GENAU EINE Sache: er beendet den Fokus. Er wird deshalb
     * geschluckt und wählt insbesondere keine andere Karte aus — das ist eine
     * zweite Entscheidung und braucht einen zweiten Klick.
     *
     * ACHTUNG, die Regel bei ungespeicherten Änderungen ist dieselbe wie bei
     * Esc, und sie muss dieselbe bleiben: es wird NICHT geschlossen. Ein Klick
     * daneben darf nicht destruktiver sein als die Taste, die dafür gedacht
     * ist. Auch dann würde die andere Karte sonst aktiv, während der Editor
     * sichtbar offen stehen bleibt und seinen Keydown-Listener verliert
     * (isActive hängt an activeMessageId). Genau dieser verwaiste Zustand war
     * der Grund für das frühere pointer-events: none auf abgedunkelten Karten.
     *
     * Einzige Ausnahme vom Schlucken: bei ungespeicherten Änderungen bleibt ein
     * Klick AUSSERHALB der Liste durchlässig. Der Fokus endet dort ohnehin
     * nicht, und ihn zu blockieren hieße, den eigenen Hinweis unwegklickbar zu
     * machen.
     */
    handleFocusModeClick(e) {
      if (this.editorOpenForId === null) return;

      // Ein Dialog liegt oben. Dieselbe Ausnahme wie suspendKeys in der Karte:
      // was im Dialog passiert, geht den Editor darunter nichts an.
      if (this.confirmState.open || this.showSystemSettingsModal) return;

      const ziel = e.target;
      if (!ziel || !document.contains(ziel)) {
        // Das Ziel ist beim Zeichnen verschwunden (die Liste hat sich unter dem
        // Klick erneuert). Dann sagt contains() unten nichts Verlässliches --
        // und ein Editor, der aus diesem Grund zuklappt, sähe wie ein
        // Zufallsfehler aus.
        return;
      }

      const comp = this.messageRefs[this.editorOpenForId];
      const karte = comp ? (comp.$el || comp) : null;
      if (karte && karte.contains(ziel)) return;

      if (comp && comp.hasUnsavedChanges) {
        const liste = this.$refs.messagesContainer;
        if (liste && liste.contains(ziel)) {
          e.preventDefault();
          e.stopPropagation();
          this.showNotice(
              "Ungespeicherte Änderungen: Shift+Enter speichert, Shift+Esc verwirft.",
              "info",
          );
        }
        return;
      }

      // Nichts zu verlieren -- der Fokus endet. Und sonst passiert nichts:
      // der Klick wird geschluckt (Simon, 08.08.).
      //
      // Warum das die bessere Regel ist: liesse man ihn weiterlaufen, waehlte
      // derselbe Klick nebenbei eine andere Karte aus, klappte dort womoeglich
      // einen Editor auf und scrollte die Liste. Wer aus einer Bewertung
      // heraussteigt, will erst einmal heraussteigen -- was danach passiert,
      // ist eine zweite Entscheidung und braucht einen zweiten Klick.
      e.preventDefault();
      e.stopPropagation();

      if (comp && typeof comp.closeEditor === "function") comp.closeEditor();
      else this.editorOpenForId = null;
    },

    showFilterHint(msg) {
      this.showNotice(msg, "info");
    },

    /**
     * Meldung ohne Entscheidung. Ersetzt alert() auf dieser Seite.
     *
     * Fehler bleiben stehen, bis jemand sie wegklickt -- ein Fehler, der sich
     * nach sechs Sekunden selbst wegraeumt, ist ein verschluckter Fehler.
     */
    showNotice(text, tone = "info") {
      if (!text) return;
      this.noticeText = text;
      this.noticeTone = tone === "error" ? "error" : "info";

      if (this.noticeTimer) clearTimeout(this.noticeTimer);
      this.noticeTimer = null;

      if (this.noticeTone !== "error") {
        this.noticeTimer = setTimeout(() => {
          this.noticeText = "";
          this.noticeTimer = null;
        }, 6000);
      }
    },

    hideNotice() {
      this.noticeText = "";
      if (this.noticeTimer) clearTimeout(this.noticeTimer);
      this.noticeTimer = null;
    },

    /**
     * Stellt eine Frage und liefert die Antwort als Promise.
     *
     * Rueckgabe: "confirm" | "extra" | "cancel". Jede Aufrufstelle wird damit
     * zu einer Zeile, ohne Aktions-Enum und Switch.
     */
    askConfirm({
      title,
      message,
      confirmLabel = "OK",
      confirmVariant = "delete",
      cancelLabel = "Abbrechen",
      extraLabel = "",
      extraVariant = "neutral",
    }) {
      // Ein zweiter Dialog waehrend eines offenen wuerde den ersten resolve
      // verlieren -- das Promise haenge dann fuer immer.
      if (this.confirmState.open) return Promise.resolve("cancel");

      this.confirmState = {
        open: true,
        title,
        message,
        confirmLabel,
        confirmVariant,
        cancelLabel,
        extraLabel,
        extraVariant,
      };
      return new Promise((resolve) => {
        this.confirmResolve = resolve;
      });
    },

    closeConfirm(antwort) {
      this.confirmState.open = false;
      const resolve = this.confirmResolve;
      this.confirmResolve = null;
      if (resolve) resolve(antwort);
    },

    /** Eine Karte meldet, dass ihr Kriterien-Editor auf- oder zugegangen ist. */
    onEditorState({ messageId, open }) {
      if (open) this.editorOpenForId = messageId;
      else if (this.editorOpenForId === messageId) this.editorOpenForId = null;
    },

    /** Eine Karte ist beim Durchtabben an die Fenstergrenze gestossen. */
    onRequestCriteriaPage(richtung) {
      if (richtung === "next") this.pageNext();
      else this.pagePrev();
    },

    /**
     * "Freigeben" bei ungespeicherten Aenderungen im Editor.
     *
     * Frueher brach hier ein alert() ab und liess den Nutzer mit dem Problem
     * allein. Jetzt gibt es drei Wege, und die Karte fuehrt den gewaehlten aus.
     */
    async onApproveBlocked(messageId) {
      const wahl = await this.askConfirm({
        title: "Ungespeicherte Änderungen",
        message:
            "Im Kriterien-Editor stehen Änderungen, die noch nicht gespeichert sind.\n"
            + "Was soll damit geschehen, bevor die Nachricht freigegeben wird?",
        confirmLabel: "Speichern und freigeben",
        confirmVariant: "primary",
        extraLabel: "Verwerfen und freigeben",
        extraVariant: "neutral",
        cancelLabel: "Abbrechen",
      });
      if (wahl === "cancel") return;

      const comp = this.messageRefs[messageId];
      if (wahl === "confirm") comp?.saveAndClose?.();
      else comp?.discardChanges?.();

      this.approve(messageId);
    },

    getMessagesFilteredForOptions({ ignoreChannel = false, ignoreCategory = false } = {}) {
      const authorId = this.filterAuthorId ? Number(this.filterAuthorId) : null;
      const teamId = this.filterTeamId ? Number(this.filterTeamId) : null;
      const channel = !ignoreChannel ? (this.filterChannel || null) : null;
      const category = !ignoreCategory ? (this.filterCategory || null) : null;

      return (this.messages || []).filter((m) => {
        if (authorId && (!m.user || m.user.id !== authorId)) return false;
        if (teamId && (!m.user || !m.user.team || m.user.team.id !== teamId)) return false;
        if (channel && m.channel !== channel) return false;
        if (category && m.category !== category) return false;
        return true;
      });
    },

    zellenDerNachricht(m) {
      // Nur die Zellen, die zur gewaehlten Session gehoeren -- dieselbe
      // Einschraenkung wie in zellStatistik. Ohne sie traefe der Filter auf
      // Kriterien zu, die auf der Seite gar nicht sichtbar sind.
      const erlaubt = this.selectedSessionId
          ? new Set((this.effectiveCriterias || []).map((c) => c.criterion?.id ?? c.id))
          : null;
      return (m.criteria_ratings || []).filter((mc) => {
        if (!erlaubt) return true;
        return erlaubt.has(mc.criterion?.id ?? mc.criterion_id);
      });
    },

    // ENTFALLEN (09.08.): der Zustand "differs" ("Modell und Mensch weichen
    // ab"). Er war von "corrected" praktisch nicht unterscheidbar und hat
    // dabei teils die falschen Zellen getroffen -- gemessen am Bestand:
    // 40 von 40 korrigierten Zellen weichen ab, beide Zustaende treffen
    // dieselben 9 Nachrichten, die Differenzmenge ist LEER. Drei Ursachen,
    // alle strukturell:
    //
    //   1. Aggregation. Gefiltert wird je NACHRICHT ueber some() -- eine
    //      Nachricht faellt nur dann heraus, wenn JEDE ihrer korrigierten
    //      Zellen eine reine Bestaetigung ist. Bei 4..6 Korrekturen je
    //      Nachricht kommt das praktisch nicht vor.
    //   2. Falsch-Positive. Die Herkunft des gesicherten Modellwerts wurde
    //      nicht geprueft: ein aus Excel getippter Wert (model_value_
    //      provenance='assumed_import') zaehlte als Modellvorhersage. Die
    //      Karte daneben sagt dazu korrekt "Herkunft unbelegt" -- zwei
    //      Aussagen auf einer Seite.
    //   3. Falsch-Negative. Zellen ohne gesicherten Modellwert konnten nie
    //      treffen, obwohl "Modell lieferte nichts, Mensch setzte +3" die
    //      staerkste Form von Abweichung ist.
    //
    // Die Information ist NICHT verloren: die Kennzahlleiste fuehrt sie als
    // Zahl "Modell != Mensch" (zellStatistik.abweichend) -- auf ZELLebene,
    // also auf der Ebene, auf der die Unterscheidung ueberhaupt existiert,
    // und mit "human ohne Modellwert" als eigener Zahl daneben.
    // Vollstaendiger Befund in der Messung vom 09.08.
    passtZumZustand(m) {
      const zellen = this.zellenDerNachricht(m);
      const korrigiert = zellen.filter((mc) => mc.score_source === "human");

      switch (this.filterReviewState) {
        case "corrected":
          return korrigiert.length > 0;
        case "untouched":
          return korrigiert.length === 0;
        default:
          return true;
      }
    },

    _clearSystemSettingsMessages() {
      this.systemSettingsSavedMessage = "";
      this.systemSettingsError = "";
      this.showPromptVersionsHint = false;
    },

    _snapshotSystemSettings() {
      this.systemSettingsInitial = JSON.parse(JSON.stringify(this.systemSettingsForm));
    },

    async openSystemSettingsModal() {
      this.showSystemSettingsModal = true;
      this._clearSystemSettingsMessages();
      // ACHTUNG, die Reihenfolge: das Modal steht SOFORT offen, die
      // Auskunft kommt danach. Solange wirksamerPrompt null ist, wird
      // kein Block als wirksam markiert und keiner aufgeklappt -- eine
      // falsche Markierung waere schlimmer als keine, denn sie
      // beantwortet genau die Frage, wegen der jemand hier ist.
      this.wirksamerPrompt = null;
      this.wirksamFehler = null;
      this.promptOffen = {};
      await Promise.all([
        this.reloadSystemSettings(),
        this.ladeWirksamenPrompt(),
      ]);
    },

    /**
     * Welcher Prompt gerade bewertet -- aus /prompt-versions/effective-key.
     *
     * Der Dialog rief das bis zum 18.08. gar nicht; die Auskunft gab es
     * nur auf der Versionsseite. Wer hier einen Prompt bearbeitete,
     * konnte den falschen erwischen und merkte es an nichts.
     *
     * ACHTUNG, ein Fehlschlag ist kein Grund, den Dialog zu sperren: die
     * Prompts bleiben bearbeitbar, nur die Markierung fehlt. Sie fehlt
     * dann aber SICHTBAR.
     */
    async ladeWirksamenPrompt() {
      try {
        const res = await getEffectivePromptKey();
        this.wirksamerPrompt = res?.data || null;
        const schluessel = this.wirksamerPrompt?.prompt_key;
        if (schluessel) this.promptOffen = { [schluessel]: true };
      } catch (e) {
        this.wirksamerPrompt = null;
        this.wirksamFehler =
            e?.response?.data?.detail || e?.message || "unbekannter Fehler";
      }
    },

    /** Einen Block auf- oder zuklappen. */
    promptBlockUmschalten(schluessel) {
      this.promptOffen = {
        ...this.promptOffen,
        [schluessel]: !this.promptOffen[schluessel],
      };
    },

    /**
     * Ob dieser Block der wirksame ist.
     *
     * Die Regel steht in promptDialog.js und wird hier nur gerufen --
     * sonst haette der Dialog eine zweite Vorstellung davon, was 'wirkt'.
     */
    istAktiverPrompt(block) {
      return istWirksam(block, this.wirksamerPrompt);
    },

    async closeSystemSettingsModal() {
      if (this.systemSettingsDirty) {
        const wahl = await this.askConfirm({
          title: "Ungespeicherte Änderungen",
          message: "Am Prompt stehen Änderungen, die noch nicht gespeichert sind. Trotzdem schließen?",
          confirmLabel: "Schließen und verwerfen",
          confirmVariant: "delete",
        });
        if (wahl !== "confirm") return;
      }
      this.showSystemSettingsModal = false;
      this._clearSystemSettingsMessages();
    },

    /**
     * Die geltende Relevanz-Vorlage holen -- den Text, den ein Lauf jetzt
     * nähme.
     *
     * ⚠️ Die Entscheidung "gilt die Vorgabe" wird hier NICHT nachgebaut.
     * Drüben zählt eine leere oder nur aus Leerzeichen bestehende Zeile als
     * nicht gesetzt; dieselbe Regel ein zweites Mal zu schreiben hieße, sie
     * eines Tages anders zu schreiben. Diese Funktion fragt nach dem Text und
     * zeigt ihn -- woher er stammt, entscheidet und beantwortet das Backend.
     *
     * Fällt die Auskunft aus, bleibt es beim Wert aus system_settings -- also
     * genau beim Verhalten von vorher. Der Ausfall wird ausgeschrieben, denn
     * dann KANN das Feld leer sein, obwohl ein Lauf die Vorgabe nähme.
     */
    async _geltendeRelevanzVorlage(ausDb) {
      this.vorlageFehler = "";
      try {
        const res = await getContextRelevanceDefault();
        const vorlage = res?.data?.vorlage;
        // Ein leerer Text ist keine Auskunft: die Vorgabe drüben ist eine
        // Konstante und kann nicht leer sein. Kommt trotzdem nichts, ist die
        // Antwort kaputt und darf nicht das Feld leeren.
        if (typeof vorlage === "string" && vorlage.trim()) {
          return vorlage;
        }
        this.vorlageFehler = "Die Antwort führt keine Vorlage.";
      } catch (e) {
        this.vorlageFehler = String(
            e?.response?.data?.detail ||
            e?.response?.data?.message ||
            e?.message ||
            e
        );
      }
      return ausDb;
    },

    async reloadSystemSettings() {
      if (this.systemSettingsDirty) {
        const wahl = await this.askConfirm({
          title: "Ungespeicherte Änderungen",
          message: "Neu laden verwirft die Änderungen am Prompt. Fortfahren?",
          confirmLabel: "Neu laden und verwerfen",
          confirmVariant: "delete",
        });
        if (wahl !== "confirm") return;
      }

      this.systemSettingsLoading = true;
      this._clearSystemSettingsMessages();

      try {
        const res = await getSystemSettings([
          "llm_prompt_template",
          "rag_prompt_template",
          "comment_prompt_template",
          "context_relevance_prompt_template",
          "async_rating_mode",
          "rag_review_scope",
          "rating_context_k",
          "rating_context_max_age_hours",
          "disable_rating_worker",
          "disable_comment_worker",
          "disable_rag_worker",
        ]);

        const data = res?.data || {};
        this.systemSettingsForm.llm_prompt_template = data.llm_prompt_template || "";
        this.systemSettingsForm.rag_prompt_template = data.rag_prompt_template || "";
        this.systemSettingsForm.comment_prompt_template = data.comment_prompt_template || "";
        // ⚠️ Hier steht bewusst NICHT der Wert aus system_settings: der
        // Relevanz-Prompt fällt ohne Zeile auf eine Konstante des
        // Bewertungs-Backends zurück, und ein leeres Feld sähe aus wie "kein
        // Prompt". Gefragt wird, welche Vorlage ein Lauf JETZT nähme -- die
        // Antwort gibt drüben dieselbe Funktion, die auch der Lauf ruft.
        this.systemSettingsForm.context_relevance_prompt_template =
            await this._geltendeRelevanzVorlage(
                data.context_relevance_prompt_template || "");
        this.systemSettingsForm.async_rating_mode = data.async_rating_mode || "";
        // Leer heißt "keine Zeile in system_settings" -- dann gilt die Vorgabe
        // 'approved'. Der leere Wert wird beim Speichern NICHT gesendet.
        this.systemSettingsForm.rag_review_scope = data.rag_review_scope || "";

        // ACHTUNG, hier steht bewusst KEIN `|| ""`: der gespeicherte Wert "0"
        // ist ein String und damit zwar wahr — aber die Absicht ist die
        // andere Richtung. Null und leer müssen unterscheidbar bleiben, und
        // ein `|| ""` an dieser Stelle lädt zu einem `|| ""` an der
        // Sendestelle ein, wo es die 0 wirklich verschluckt.
        this.systemSettingsForm.rating_context_k = this._zahlAusDb(data.rating_context_k);
        this.systemSettingsForm.rating_context_max_age_hours =
            this._zahlAusDb(data.rating_context_max_age_hours);

        this.systemSettingsForm.disable_rating_worker = this._toBoolDb(data.disable_rating_worker);
        this.systemSettingsForm.disable_comment_worker = this._toBoolDb(data.disable_comment_worker);
        this.systemSettingsForm.disable_rag_worker = this._toBoolDb(data.disable_rag_worker);

        this._snapshotSystemSettings();
      } catch (e) {
        const detail =
            e?.response?.data?.detail ||
            e?.response?.data?.message ||
            e?.message ||
            "Failed to load system settings";
        this.systemSettingsError = String(detail);
      } finally {
        this.systemSettingsLoading = false;
      }
    },

    async saveSystemSettings() {
      this.systemSettingsSaving = true;
      this._clearSystemSettingsMessages();

      try {
        const payload = {};

        if (!this.systemSettingsInitial) {
          if (this.systemSettingsForm.llm_prompt_template.trim())
            payload.llm_prompt_template = this.systemSettingsForm.llm_prompt_template;
          if (this.systemSettingsForm.rag_prompt_template.trim())
            payload.rag_prompt_template = this.systemSettingsForm.rag_prompt_template;
          if (this.systemSettingsForm.comment_prompt_template.trim())
            payload.comment_prompt_template = this.systemSettingsForm.comment_prompt_template;
          if (this.systemSettingsForm.context_relevance_prompt_template.trim())
            payload.context_relevance_prompt_template =
                this.systemSettingsForm.context_relevance_prompt_template;
          if (this.systemSettingsForm.async_rating_mode)
            payload.async_rating_mode = this.systemSettingsForm.async_rating_mode;
          if (this.systemSettingsForm.rag_review_scope)
            payload.rag_review_scope = this.systemSettingsForm.rag_review_scope;

          this._ergaenzeKontextfelder(payload);

          payload.disable_rating_worker = this._toDb01(this.systemSettingsForm.disable_rating_worker);
          payload.disable_comment_worker = this._toDb01(this.systemSettingsForm.disable_comment_worker);
          payload.disable_rag_worker = this._toDb01(this.systemSettingsForm.disable_rag_worker);
        } else {
          if (this.systemSettingsForm.llm_prompt_template !== this.systemSettingsInitial.llm_prompt_template) {
            payload.llm_prompt_template = this.systemSettingsForm.llm_prompt_template;
          }
          if (this.systemSettingsForm.rag_prompt_template !== this.systemSettingsInitial.rag_prompt_template) {
            payload.rag_prompt_template = this.systemSettingsForm.rag_prompt_template;
          }
          if (this.systemSettingsForm.comment_prompt_template !== this.systemSettingsInitial.comment_prompt_template) {
            payload.comment_prompt_template = this.systemSettingsForm.comment_prompt_template;
          }
          if (
            this.systemSettingsForm.context_relevance_prompt_template !==
            this.systemSettingsInitial.context_relevance_prompt_template
          ) {
            payload.context_relevance_prompt_template =
                this.systemSettingsForm.context_relevance_prompt_template;
          }
          if (this.systemSettingsForm.async_rating_mode !== this.systemSettingsInitial.async_rating_mode) {
            payload.async_rating_mode = this.systemSettingsForm.async_rating_mode;
          }
          // ACHTUNG, die zusätzliche Bedingung auf den leeren Wert: das Feld
          // ist ein Enum, und "" ist kein gültiger Wert -- ein PUT damit
          // liefe in 422. "(not set)" ist auch gar keine Einstellung, die sich
          // schreiben ließe: der Handler kann upserten, nicht löschen. Wer
          // zurück zur Vorgabe will, wählt 'approved' -- das ist derselbe
          // Zustand mit einer Zeile mehr.
          //
          // Dieselbe Falle steht eine Bedingung weiter oben bei
          // async_rating_mode und ist dort NICHT mitgefixt: gemeldeter
          // Bestandsdefekt, kein Aufräumauftrag dieses Pakets.
          if (
            this.systemSettingsForm.rag_review_scope &&
            this.systemSettingsForm.rag_review_scope !== this.systemSettingsInitial.rag_review_scope
          ) {
            payload.rag_review_scope = this.systemSettingsForm.rag_review_scope;
          }

          this._ergaenzeKontextfelder(payload, this.systemSettingsInitial);

          if (this.systemSettingsForm.disable_rating_worker !== this.systemSettingsInitial.disable_rating_worker) {
            payload.disable_rating_worker = this._toDb01(this.systemSettingsForm.disable_rating_worker);
          }
          if (this.systemSettingsForm.disable_comment_worker !== this.systemSettingsInitial.disable_comment_worker) {
            payload.disable_comment_worker = this._toDb01(this.systemSettingsForm.disable_comment_worker);
          }
          if (this.systemSettingsForm.disable_rag_worker !== this.systemSettingsInitial.disable_rag_worker) {
            payload.disable_rag_worker = this._toDb01(this.systemSettingsForm.disable_rag_worker);
          }
        }

        if (!Object.keys(payload).length) {
          this.systemSettingsSavedMessage = "No changes to save.";
          setTimeout(() => {
            this.systemSettingsSavedMessage = "";
          }, 2000);
          return;
        }

        await updateSystemSettings(payload);

        // Die beiden Bewertungsprompts werden seit R3 NICHT mehr direkt
        // wirksam: das Backend legt daraus eine Entwurfsversion an, und erst
        // ein bestandener Regressionsvergleich schaltet sie frei. Das GET im
        // reload liefert deshalb weiter den alten, aktiven Text -- ohne diesen
        // Hinweis sieht das nach "hat nicht gespeichert" aus.
        const entwuerfe = PROMPT_VERSION_KEYS.filter((k) => k in payload);
        const sofortWirksam = Object.keys(payload).filter(
          (k) => !PROMPT_VERSION_KEYS.includes(k)
        );

        // Der Stand ist jetzt gespeichert -- der Schnappschuss muss das
        // widerspiegeln, BEVOR neu geladen wird. Sonst hält
        // reloadSystemSettings den Stand für ungespeichert und fragt
        // "Reload and discard changes?" -- nach einem Speichern.
        // Klickt der Nutzer dort auf Abbrechen, bricht der Reload ab, das Feld
        // behält den Entwurfstext, und die Meldung unten wäre unwahr.
        this._snapshotSystemSettings();

        // Erst neu laden, dann melden. reloadSystemSettings ruft
        // _clearSystemSettingsMessages -- eine vorher gesetzte Meldung wäre
        // sofort wieder weg. (Das galt auch für das bisherige "Saved ✓": es
        // wurde nie sichtbar.)
        await this.reloadSystemSettings();

        // Ein Fehler beim Neuladen darf keine Erfolgsmeldung danebenstellen.
        if (this.systemSettingsError) return;

        if (entwuerfe.length) {
          let text =
            "Als Entwurf gespeichert. Der Bewertungsprompt ändert sich dadurch noch " +
            "nicht — die Version muss erst den Regressionsvergleich bestehen. Das Feld " +
            "zeigt deshalb wieder den aktiven Text.";
          if (sofortWirksam.length) {
            // Sonst behauptet die Meldung für die ganze Speicherung, was nur
            // für die Prompts gilt -- Modus und Worker-Schalter sind sofort scharf.
            text +=
              " Die übrigen Einstellungen (" +
              sofortWirksam.join(", ") +
              ") sind dagegen sofort wirksam.";
          }
          this.systemSettingsSavedMessage = text;
          this.showPromptVersionsHint = true;
        } else {
          this.systemSettingsSavedMessage = "Saved ✓";
          setTimeout(() => {
            this.systemSettingsSavedMessage = "";
          }, 2500);
        }
      } catch (e) {
        const detail =
            e?.response?.data?.detail ||
            e?.response?.data?.message ||
            e?.message ||
            "Failed to save system settings";
        this.systemSettingsError = String(detail);
      } finally {
        this.systemSettingsSaving = false;
      }
    },

    normalizeSelectValue(val) {
      if (val == null) return "";
      if (typeof val === "string") return val;
      if (typeof val === "number") return String(val);
      if (typeof val === "object") {
        if (val.value != null) return String(val.value);
        if (val.id != null) return String(val.id);
        return "";
      }
      return "";
    },

    loadPersistedToPending() {
      try {
        const storedHide = localStorage.getItem("async_messages_hideReviewed");
        this.pendingFilters.hideReviewed =
            storedHide !== null ? (storedHide === "1" || storedHide === "true") : null;

        this.pendingFilters.filterAuthorId =
            localStorage.getItem("async_messages_filterAuthorId");
        this.pendingFilters.filterTeamId =
            localStorage.getItem("async_messages_filterTeamId");
        this.pendingFilters.filterChannel =
            localStorage.getItem("async_messages_filterChannel");
        this.pendingFilters.filterCategory =
            localStorage.getItem("async_messages_filterCategory");
        this.pendingFilters.filterReviewState =
            localStorage.getItem("async_messages_filterReviewState");
        this.pendingFilters.selectedSessionId =
            localStorage.getItem("async_messages_selectedSessionId");
      } catch (e) {
        console.warn("Failed to read persisted filters", e);
      }
    },

    applyPendingSession() {
      // Nur die Session, und ausdruecklich VOR dem ersten fetchMessages().
      //
      // Frueher lief fetchMessages() im Promise.all von mounted(), also vor
      // der Wiederherstellung aus dem localStorage. Seit der Server nach
      // Session filtert, waere der Erstaufruf damit ungefiltert und der
      // Watcher zoege nach: zwei volle Ladungen, und dazwischen blitzt die
      // ungefilterte Liste auf.
      //
      // Getrennt von applyPendingFilters(), weil die uebrigen Filter ihre
      // Auswahlmengen (Verfasser, Kategorie, Kanal) aus den GELADENEN
      // Nachrichten ableiten -- die gibt es an dieser Stelle noch nicht.
      this.isRestoring = true;
      const s = this.normalizeSelectValue(this.pendingFilters.selectedSessionId);
      const inOptions = (val, opts) => opts.some((o) => String(o.value) === String(val));
      if (s && inOptions(s, this.sessionOptions)) this.selectedSessionId = s;
      else this.selectedSessionId = "";
      this.isRestoring = false;
    },

    applyPendingFilters() {
      this.isRestoring = true;

      if (this.pendingFilters.hideReviewed !== null) {
        this.hideReviewed = !!this.pendingFilters.hideReviewed;
      }

      const a = this.normalizeSelectValue(this.pendingFilters.filterAuthorId);
      const t = this.normalizeSelectValue(this.pendingFilters.filterTeamId);
      const ch = this.normalizeSelectValue(this.pendingFilters.filterChannel);
      const c = this.normalizeSelectValue(this.pendingFilters.filterCategory);
      const rs = this.normalizeSelectValue(this.pendingFilters.filterReviewState);

      const inOptions = (val, opts) => opts.some((o) => String(o.value) === String(val));

      if (rs && inOptions(rs, this.reviewStateOptions)) this.filterReviewState = rs;
      else this.filterReviewState = "";

      // Die Session wird hier NICHT mehr gesetzt -- das erledigt
      // applyPendingSession() schon vor dem ersten fetchMessages(),
      // damit die Liste gar nicht erst ungefiltert geladen wird.

      if (a && inOptions(a, this.authorOptions)) this.filterAuthorId = a;
      else this.filterAuthorId = "";

      if (t && inOptions(t, this.teamOptions)) this.filterTeamId = t;
      else this.filterTeamId = "";

      if (c && inOptions(c, this.categoryOptions)) this.filterCategory = c;
      else this.filterCategory = "";

      if (ch && inOptions(ch, this.channelOptions)) this.filterChannel = ch;
      else this.filterChannel = "";

      this.fetchCriteriasBySession();
      this.criteriaWindowStart = 0;

      this.isRestoring = false;
    },

    pagePrev() {
      if (!this.canPagePrev) return;
      this.criteriaWindowStart = Math.max(0, this.criteriaWindowStart - this.criteriaWindowSize);
    },
    pageNext() {
      if (!this.canPageNext) return;
      const len = this.effectiveCriterias.length || 0;
      const newStart = this.criteriaWindowStart + this.criteriaWindowSize;
      this.criteriaWindowStart = Math.min(newStart, Math.max(0, len - this.criteriaWindowSize));
    },

    setMessageRef(id, el) {
      if (!el) delete this.messageRefs[id];
      else this.messageRefs[id] = el;
    },

    // Den Warteschlangenstand holen — und die volle Liste NUR, wenn sich
    // etwas geändert hat.
    //
    // ⚠️ Das ist der Kern des Nachladens. Ein blindes fetchMessages() im Takt
    // wäre 2,62 MB je Aufruf (gemessen, 227 Nachrichten) — bei 10 s Takt rund
    // 16 MB je Minute, für eine Liste, die sich meist nicht ändert. Diese
    // Auskunft ist 241 Byte groß (live gemessen).
    //
    // `stand` ändert sich genau dann, wenn Bewertungszeilen dazukommen oder
    // verschwinden. Beim allerersten Durchlauf wird NICHT nachgeladen — die
    // Liste ist da gerade frisch aus mounted().
    async fetchRatingStatus() {
      const a = await getRatingStatus();
      const neu = a?.data || null;
      const vorher = this.ratingStatus;
      this.ratingStatus = neu;

      if (!vorher || !neu) return;
      if (vorher.stand !== neu.stand) {
        await this.fetchMessages();
      }
    },

    /**
     * Den Vektorzustand holen — und die volle Liste NUR bei einer Änderung.
     *
     * ⚠️ Dieselbe Bauart wie fetchRatingStatus, mit demselben Grund: der
     * Zustand steckt als `message.vectorized` in den 2,62 MB von
     * GET /messages, die Auskunft daneben ist 261 Byte groß (live gemessen).
     *
     * ⚠️ `stand` trägt hier die drei Eimergrößen und das jüngste
     * Wasserzeichen. Bei einem Sieben-Tage-Takt feuert das fast nie — das ist
     * der gewollte Handel und keine Nachlässigkeit.
     *
     * ⚠️ Beim ersten Durchlauf wird NICHT nachgeladen: die Liste kommt
     * gerade frisch aus mounted().
     */
    async fetchRagStatus() {
      const a = await getRagStatus();
      const neu = a?.data || null;
      const vorher = this.ragStatus;
      this.ragStatus = neu;

      if (!vorher || !neu) return;
      if (vorher.stand !== neu.stand) {
        await this.fetchMessages();
      }
    },

    async fetchMessages() {
      this.loading = true;
      try {
        // Die gewaehlte Session verengt die Liste SERVERSEITIG.
        //
        // Der Schluessel wird WEGGELASSEN, nicht leer gesetzt: axios
        // serialisiert { session_id: "" } zu ?session_id= , und FastAPI
        // antwortet darauf gegen Optional[int] mit 422. Der Wert kommt aus
        // BaseSelect als String, deshalb Number().
        const params = this.selectedSessionId
          ? { session_id: Number(this.selectedSessionId) }
          : {};
        const res = await listMessages(params);
        const msgs = res.data || [];
        this.messages = msgs;
        // Erst NACH der erfolgreichen Zuweisung zurücksetzen. Andernfalls
        // stünde zwischen dem Löschen und einem erneuten Fehlschlag ein
        // Zeitfenster, in dem weder Liste noch Fehler etwas behaupten.
        this.ladeFehler = null;

        if (!msgs.length) {
          this.lastUpdate = null;
        } else {
          const newest = msgs.reduce((latest, msg) => {
            const cur = new Date(msg?.sent_at ?? msg?.created_at ?? 0);
            if (Number.isNaN(cur.getTime())) return latest;
            if (!latest) return msg;
            const lat = new Date(latest?.sent_at ?? latest?.created_at ?? 0);
            return cur > lat ? msg : latest;
          }, null);

          this.lastUpdate = newest ? (newest.sent_at ?? newest.created_at ?? null) : null;
        }
      } catch (e) {
        console.error("Error loading messages", e);
        this.lastUpdate = null;
        // ⚠️ Bis hierher endete der Fehlerfall in der Konsole, und die Seite
        // fiel auf `!loading && messages.length === 0` zurück — sie behauptete
        // dann „Keine Nachrichten gefunden.", also eine Aussage über den
        // Bestand, die sie nie getroffen hat. Der Grund wird jetzt festgehalten
        // und im Template ausgeschrieben.
        //
        // ⚠️ `this.messages` wird NICHT geleert: steht bereits ein Stand da,
        // ist er die letzte belegte Wahrheit. Ihn wegen eines fehlgeschlagenen
        // Nachladens zu verwerfen, machte aus einem Aktualitätsproblem einen
        // Datenverlust auf dem Schirm.
        this.ladeFehler = ladeFehlerGrund(e);

        // ⚠️ Der Block unter der Liste erscheint nur, wenn dort NICHTS steht.
        // Scheitert das Nachladen bei vorhandenem Stand, hätte sonst niemand
        // etwas gesehen — die Liste sähe aus wie eben, wäre aber eingefroren.
        // Der Streifen ist dafür das richtige Mittel: er überlagert die Liste
        // nicht, und mit `error` bleibt er stehen statt nach 6 s zu verfallen.
        if (this.messages.length) {
          this.showNotice(
            this.ladeFehler
              ? `${LADEN.fehler} ${this.ladeFehler}`
              : LADEN.fehler,
            "error"
          );
        }
      } finally {
        this.loading = false;
      }
    },

    async fetchCriterias() {
      try {
        const res = await getCriterias();
        this.criterias = res.data;
      } catch (e) {
        console.error("Error loading criteria", e);
      }
    },

    async fetchSessions() {
      try {
        const res = await getSessions();
        this.sessions = res.data || [];
      } catch (err) {
        console.error("Failed to load sessions:", err);
      }
    },

    async triggerBotSync() {
      this.triggeringBot = true;
      this.botTriggerMessage = "";
      try {
        const res = await triggerMessageBotInitialSync();
        const payload = res?.data || {};
        const message =
            payload.message || payload.detail || payload.status || "Message bot sync triggered";
        this.botTriggerMessage = message;
      } catch (err) {
        const detail =
            err?.response?.data?.detail ||
            err?.response?.data?.message ||
            err?.message ||
            "Message bot sync failed";
        this.botTriggerMessage = detail;
      } finally {
        this.triggeringBot = false;
        if (this.botTriggerMessage) setTimeout(() => { this.botTriggerMessage = ""; }, 5000);
      }
    },

    async fetchCriteriasBySession() {
      if (!this.selectedSessionId) {
        this.sessionCriterias = [];
        return;
      }

      const sessionIdNum = Number(this.selectedSessionId);

      const flattenAllSessions = (sessions) =>
          (sessions || []).flatMap((s) => [s, ...(s.children ? flattenAllSessions(s.children) : [])]);

      const allSessions = flattenAllSessions(this.sessions);
      const session = allSessions.find((s) => s.id === sessionIdNum);

      if (!session || !Array.isArray(session.criteria)) {
        this.sessionCriterias = [];
        return;
      }

      const map = new Map();
      session.criteria.forEach((c) => {
        if (!c.criterion || c.criterion.id == null) return;
        const cid = c.criterion.id;
        const weight = typeof c.weight === "number" ? c.weight : null;

        if (!map.has(cid)) {
          map.set(cid, {
            criterion: c.criterion,
            sumWeight: weight != null ? weight : 0,
            count: weight != null ? 1 : 0,
          });
        } else {
          const entry = map.get(cid);
          if (weight != null) {
            entry.sumWeight += weight;
            entry.count += 1;
          }
        }
      });

      const uniqueCriteria = Array.from(map.values()).map((entry) => {
        const avgWeight = entry.count > 0 ? entry.sumWeight / entry.count : null;
        return { ...entry.criterion, weight: avgWeight };
      });

      this.sessionCriterias = uniqueCriteria.sort((a, b) =>
          a.name.localeCompare(b.name, "en", { sensitivity: "base" })
      );
    },

    async ladeAndereServer() {
      this.andere = null;
      if (!this.selectedSessionId) return;
      const angefragt = Number(this.selectedSessionId);
      try {
        const res = await getAndereServer(angefragt);
        // Schneller Session-Wechsel: eine spaete Antwort der VORIGEN
        // Session darf die Zeile der jetzt gewaehlten nicht ueberschreiben.
        if (Number(this.selectedSessionId) !== angefragt) return;
        this.andere = res.data;
      } catch (e) {
        // Kein Rueckfall auf einen erfundenen Umfang: ohne Antwort bleibt
        // die Umfangszeile weg.
        console.warn("Andere Server der Session nicht ladbar", e);
      }
    },

    async endSelectedSession() {
      const s = this.selectedSessionObj;
      if (!s || this.selectedSessionEnded) return;

      // ⚠️ Der Comment-Worker schreibt fuer Studenten ohne Nachrichten im
      //    Umfang nichts und meldet das nirgends (comment_worker.py:281);
      //    beendet wird trotzdem. Der Satz steht VOR dem Beenden.
      const ohne = this.messages.length === 0 ? `\n\n${TREFFER.beendenOhneNachrichten}` : "";
      const wahl = await this.askConfirm({
        title: "Session beenden",
        message: `Session „${s.title}“ beenden?${ohne}`,
        confirmLabel: "Beenden",
        confirmVariant: "primary",
      });
      if (wahl !== "confirm") return;

      this.isEndingSession = true;
      try {
        const payload = {
          title: s.title,
          description: s.description,
          parent_id: s.parent_id,
          start_date: s.start_date,
          end_date: s.end_date,
          is_async: s.is_async,
          ended: true,
          criteria: (s.criteria || []).map((c) => ({
            id: c?.criterion?.id ?? c?.id,
            role_id: c?.role_id,
            weight: typeof c?.weight === "number" ? c.weight : 1,
          })),
        };

        await updateSession(s.id, payload);
        await this.fetchSessions();
      } catch (e) {
        console.error("Failed to end session", e);
        this.showNotice("Session konnte nicht beendet werden.", "error");
      } finally {
        this.isEndingSession = false;
      }
    },

    async approve(messageId) {
      try {
        const res = await approveMessage(messageId);
        const idx = this.messages.findIndex((m) => m.id === messageId);
        if (idx !== -1) {
          const current = this.messages[idx];
          current.reviewed = this._toBoolDb(res?.data?.reviewed);
          this.messages.splice(idx, 1, { ...current });
        }
      } catch (e) {
        console.error("Approval failed", e);
      }
    },

    async rate({ messageId, criterionId, value, comment }) {
      try {
        const res = await setMessageCriterionRating(messageId, criterionId, value, comment);
        const idx = this.messages.findIndex((m) => m.id === messageId);
        if (idx !== -1) {
          const msg = { ...this.messages[idx] };
          const mcIdx = (msg.criteria_ratings || []).findIndex(
              (c) => c.criterion_id === criterionId || (c.criterion && c.criterion.id === criterionId)
          );
          if (mcIdx !== -1) msg.criteria_ratings[mcIdx] = res.data;
          else msg.criteria_ratings = [...(msg.criteria_ratings || []), res.data];
          this.messages.splice(idx, 1, msg);
        }
      } catch (e) {
        console.error("Failed to set rating/comment", e);
      }
    },

    async rateAgain(messageId) {
      const idx = this.messages.findIndex((m) => m.id === messageId);
      if (idx === -1) return;

      const before = this.messages[idx];

      // Das Backend löscht alle message_criteria dieser Nachricht – inklusive
      // manuell korrigierter Werte. Das ist kein Versehen, sondern der Auslöser:
      // der RatingWorker wählt seine Arbeit über
      // coalesce(rated_count,0) < total_criterias, nicht über reviewed.
      // Bleiben die Zeilen stehen, wird die Nachricht nie wieder bewertet.
      //
      // ACHTUNG: Das ist der EINZIGE Dialog dieses Wegs. Bis hierher fragte
      // MessageCard vorher noch einmal, auf Englisch und ohne die Konsequenz zu
      // nennen -- wer dort bestaetigte, hatte den entscheidenden Satz noch gar
      // nicht gelesen. Die Frage gehoert hierher, weil nur hier bekannt ist, ob
      // ueberhaupt Bewertungen vorliegen.
      const hasRatings = (before.criteria_ratings || []).length > 0;
      const anzahl = (before.criteria_ratings || []).length;

      // Der Text nennt die HANDLUNG und ihren Preis, sonst nichts. Warum
      // geloescht werden muss (die Auswahlregel des RatingWorkers), erklaert der
      // Tooltip am Knopf -- es aendert die Entscheidung nicht, und ein Dialog,
      // den man ueberfliegt, hat seinen Zweck verfehlt.
      const wahl = await this.askConfirm({
        title: "Neu bewerten",
        message: hasRatings
            ? `Nachricht #${messageId} wird neu bewertet.\n\n`
              + `Die ${anzahl} vorhandenen Bewertungen werden dabei gelöscht — `
              + "auch die von Hand korrigierten. Sie bleiben im Archiv."
            : `Nachricht #${messageId} wird neu bewertet. `
              + "Es liegen keine Bewertungen vor, es geht nichts verloren.",
        confirmLabel: hasRatings ? "Löschen und neu bewerten" : "Neu bewerten",
        confirmVariant: hasRatings ? "delete" : "primary",
      });
      if (wahl !== "confirm") return;

      try {
        const res = await rateAgainMessage(messageId);

        // Kein Optimistic Update: der lokale Stand muss dem entsprechen, was das
        // Backend tatsächlich getan hat.
        const reviewed = res?.data?.reviewed ?? false;
        this.messages.splice(idx, 1, {
          ...before,
          reviewed,
          criteria_ratings: [],
        });

        // Erst JETZT den Editor schliessen -- nicht schon beim Klick. Bricht der
        // Nutzer ab, bleibt sein Editor stehen.
        this.messageRefs[messageId]?.closeEditor?.();
      } catch (e) {
        console.error("Rate again failed", e);
        this.showNotice("Neu bewerten fehlgeschlagen.", "error");
      }
    },


    async deleteMsg(messageId) {
      const wahl = await this.askConfirm({
        title: "Nachricht löschen",
        message:
            `Nachricht #${messageId} wird endgültig gelöscht, mit allen `
            + "Bewertungen und Kommentaren. Das lässt sich nicht rückgängig machen.",
        confirmLabel: "Endgültig löschen",
        confirmVariant: "delete",
      });
      if (wahl !== "confirm") return;

      try {
        await deleteMessage(messageId);

        const hadActive = this.activeMessageId === messageId;
        this.messages = (this.messages || []).filter((m) => m.id !== messageId);

        if (hadActive) {
          this.$nextTick(() => {
            this.activeMessageId = this.visibleMessages[0]?.id ?? null;
            this.scrollActiveIntoView();
          });
        }
      } catch (e) {
        console.error("Delete failed.", e);
        this.showNotice("Löschen fehlgeschlagen.", "error");
      }
    },

    setActiveMessage(id) {
      if (this.activeMessageId === id) return;
      this.activeMessageId = id;
      this.$nextTick(() => this.scrollActiveIntoView());
    },

    scrollToMessage(messageId) {
      const targetMessage = this.visibleMessages.find(m => m.id === messageId);
      if (!targetMessage) {
        console.warn(`Referenced message #${messageId} not found in visible messages`);
        return;
      }

      this.activeMessageId = messageId;

      this.$nextTick(() => {
        const comp = this.messageRefs[messageId];
        if (!comp) return;

        const el = comp.$el ? comp.$el : comp;
        if (!el || typeof el.scrollIntoView !== "function") return;

        // Nicht mehr selbst rechnen: der Scrollbereich ist seit dem Layout-Fix
        // ein Vorfahre, nicht die Liste. scrollIntoView bedient jeden davon.
        el.scrollIntoView({ block: "center", behavior: "smooth" });
      });
    },

    moveActiveMessage(delta) {
      const list = this.visibleMessages;
      if (!list.length) return;
      const currentIndex = list.findIndex((m) => m.id === this.activeMessageId);
      const idx = currentIndex === -1 ? 0 : currentIndex;
      let nextIndex = idx + delta;
      if (nextIndex < 0) nextIndex = 0;
      if (nextIndex >= list.length) nextIndex = list.length - 1;
      this.activeMessageId = list[nextIndex].id;
      this.$nextTick(() => this.scrollActiveIntoView());
    },

    scrollActiveIntoView() {
      if (!this.activeMessageId) return;

      const comp = this.messageRefs[this.activeMessageId];
      if (!comp) return;

      const el = comp.$el ? comp.$el : comp;
      if (!el || typeof el.scrollIntoView !== "function") return;

      // 'nearest' ruehrt sich nur, wenn die Karte wirklich ausserhalb liegt -
      // dieselbe Zusicherung wie die frueher von Hand gerechnete Variante.
      el.scrollIntoView({ block: "nearest" });
    },

    // Der Scrollbereich ist nicht mehr die Liste selbst, sondern der naechste
    // scrollende Vorfahre (in App.vue das <main>). Ihn zu suchen statt ihn zu
    // kennen haelt den Tastaturweg gueltig, wenn das Layout sich wieder aendert.
    scrollPort() {
      let el = this.$refs.messagesContainer;
      while (el && el !== document.body && el !== document.documentElement) {
        const overflowY = window.getComputedStyle(el).overflowY;
        if (
            (overflowY === "auto" || overflowY === "scroll") &&
            el.scrollHeight > el.clientHeight
        ) {
          return el;
        }
        el = el.parentElement;
      }
      return document.scrollingElement || document.documentElement;
    },

    scrollMessages(delta) {
      const port = this.scrollPort();
      if (!port) return;
      const amount = 200 * delta;
      port.scrollBy({ top: amount, behavior: "smooth" });
    },

    async resetActiveMessageRatingsToZero() {
      const id = this.activeMessageId;
      if (!id) return;

      const comp = this.messageRefs[id];
      if (!comp || typeof comp.getResetAllRatingsPayload !== "function") {
        console.warn("Active MessageCard ref missing or does not support reset payload");
        return;
      }

      const info = comp.getResetAllRatingsPayload();
      if (!info || info.ok === false) {
        if (info?.reason) this.showNotice(info.reason, "error");
        return;
      }

      const { messageId, items } = info;
      if (!items || !items.length) {
        this.showNotice(
            "Keine Bewertungen zum Zurücksetzen — es ist keine Session mit Kriterien gewählt.",
            "error",
        );
        return;
      }

      const wahl = await this.askConfirm({
        title: "Alle Bewertungen auf 0",
        message:
            `Alle ${items.length} Bewertungen von Nachricht #${messageId} werden auf 0 gesetzt.\n\n`
            + "Die Kommentare bleiben unverändert.",
        confirmLabel: "Auf 0 setzen",
        confirmVariant: "delete",
      });
      if (wahl !== "confirm") return;

      for (const it of items) {
        await this.rate({
          messageId,
          criterionId: it.criterionId,
          value: 0,
          comment: it.comment ?? "",
        });
      }
    },

    handleGlobalKeydown(e) {
      // ACHTUNG: Hier stand ein e.preventDefault() auf JEDES Tab. Damit war die
      // Seite mit der Tastatur nicht bedienbar -- weder die Filterleiste noch
      // die Textfelder des Prompt-Modals. Tab gehoert jetzt der aktiven Karte
      // (MessageCard.onKeydown), die ihn nur im eigenen Bereich verbraucht;
      // ueberall sonst ist natives Tab genau richtig.
      //
      // Das damalige e.stopPropagation() war ohnehin wirkungslos: beide
      // Listener haengen am selben Target, dafuer braeuchte es
      // stopImmediatePropagation.

      // Liegt ein Dialog oben, hoert die Liste nicht mit. Sonst feuerten j/k
      // und Shift+0 auch bei offenem Prompt-Modal.
      if (this.confirmState.open || this.showSystemSettingsModal) return;

      if (this.showInfoPopover && e.key === "Escape") {
        this.closeInfoPopover();
        e.preventDefault();
        return;
      }

      const isInFilterArea = !!(e.target && e.target.closest && e.target.closest(".filter-area"));
      const isArrowKey = ["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight"].includes(e.key);

      // Allow OS “open select” shortcut: Alt + ArrowDown/Up when focused on a native select
      const tag = (e.target && e.target.tagName ? e.target.tagName : "").toLowerCase();
      const isNativeSelect = tag === "select";

      if (isInFilterArea && isArrowKey) {
        // If it's a native select, allow Alt+ArrowDown/Up to open the dropdown.
        if (isNativeSelect && e.altKey && (e.key === "ArrowDown" || e.key === "ArrowUp")) {
          return;
        }

        // Otherwise, block arrows inside the filter area.
        e.preventDefault();
        return;
      }

      // If user is typing, never hijack keys
      if (["input", "textarea", "select"].includes(tag) || (e.target && e.target.isContentEditable)) return;

      const key = (e.key || "").toLowerCase();

      if (!e.ctrlKey && !e.metaKey && !e.altKey && e.shiftKey && e.code === "Digit0") {
        if (!this.activeMessageId) {
          this.showNotice("Keine Nachricht ausgewählt.", "error");
          e.preventDefault();
          return;
        }

        this.resetActiveMessageRatingsToZero();
        e.preventDefault();
        return;
      }

      if (e.ctrlKey && e.shiftKey && (key === "arrowdown" || key === "arrowup")) {
        this.scrollMessages(key === "arrowdown" ? 1 : -1);
        e.preventDefault();
        return;
      }

      if (!e.ctrlKey && !e.shiftKey && (key === "arrowdown" || key === "arrowup")) {
        this.moveActiveMessage(key === "arrowdown" ? 1 : -1);
        e.preventDefault();
        return;
      }

      if (key === "j") {
        this.moveActiveMessage(1);
        e.preventDefault();
      } else if (key === "k") {
        this.moveActiveMessage(-1);
        e.preventDefault();
      }
    },
  },

  async mounted() {
    this.loadPersistedToPending();

    await Promise.all([
      this.fetchCriterias(),
      this.fetchSessions(),
    ]);

    // Erst die Session, dann laden: sonst holt der erste Aufruf den
    // vollen Bestand und der Watcher muesste ihn sofort ersetzen.
    this.applyPendingSession();
    await this.fetchMessages();
    // Der Watcher schweigt waehrend der Wiederherstellung (isRestoring) --
    // die anderen Server fuer die wiederhergestellte Session hier nachladen.
    this.ladeAndereServer();

    this.applyPendingFilters();

    // Den Warteschlangenstand einmal holen und danach im Takt des Workers.
    //
    // ⚠️ Das Intervall kommt VOM DIENST (poll_sekunden) und steht nicht hier.
    // Schneller als der Worker pollt, kann nichts Neues erscheinen — eine
    // Zahl in dieser Datei wäre die zweite Wahrheit neben RATING_WORKER_POLL.
    // Untergrenze 5 s als Schutz gegen eine versehentlich winzige Vorgabe.
    try {
      await this.fetchRatingStatus();
    } catch (e) {
      // Kein Abbruch: die Liste steht auch ohne diese Auskunft. Nur die
      // Pillen fehlen dann, und das ist der ehrlichere Zustand als eine
      // geratene Angabe.
      console.warn("Warteschlangenstand nicht abrufbar", e);
    }
    const takt = Math.max(5000, (this.ratingStatus?.poll_sekunden || 10) * 1000);
    // Bewusst NICHT in data(): der Poller ist kein Zustand der Anzeige,
    // sondern ein Handgriff aufs Zeitgeber-Objekt. Reaktiv gemacht löste er
    // bei jedem Takt ein Rendern aus — und der Linter verbietet führende
    // Unterstriche in data() ohnehin (vue/no-reserved-keys). Dieselbe
    // Überlegung wie bei _ladenr in ReflectionPage.vue.
    // ⚠️ mounted() ist async und hat oben auf 2,6 MB gewartet. Wer die Seite
    // in dieser Zeit verlässt, hat beforeUnmount() bereits hinter sich — mit
    // `_polling === undefined`, also ohne dass irgendetwas gestoppt worden
    // wäre. Der Takt entstünde danach an einer zerstörten Komponente und
    // liefe den Rest der Sitzung weiter; jedes erneute Betreten legte einen
    // weiteren solchen an.
    if (this._entladen) return;
    // ⚠️ sofort: false. Der erste Abruf ist oben schon gelaufen — er musste
    // es, denn erst seine Antwort nennt poll_sekunden. Ohne den Schalter
    // fragte start() ein zweites Mal, unmittelbar hintereinander, und diese
    // zweite Abfrage hätte ein „vorher": schreibt der Worker dazwischen eine
    // Zeile, löste sie direkt nach dem Mounten ein volles fetchMessages()
    // über 2,6 MB aus — genau das, was der schlanke Endpunkt vermeiden soll.
    this._polling = usePolling(this.fetchRatingStatus, takt, { sofort: false });
    this._polling.start(takt);

    // Der Vektorzustand, EIGENER Takt und ein deutlich langsamerer.
    //
    // ⚠️ Nicht in den Block oben mit hineingenommen: der Bewertungslauf pollt
    // alle 10 s, der Vektorlauf steht in der Vorgabe auf 604800 s (sieben
    // Tage). Ein zweiter 5-Sekunden-Takt fragte tausendfach nach einer
    // Antwort, die sich fast nie ändert. Die Untergrenze ist deshalb 30 s,
    // und der Dienst darf sie nur nach OBEN verschieben.
    //
    // ⚠️ Auch hier `sofort: false` — der erste Wert kommt aus dem Aufruf
    // darüber, aus demselben Grund wie beim Zwilling.
    await this.fetchRagStatus();
    if (this._entladen) return;
    const ragTakt = Math.max(30000, (this.ragStatus?.poll_sekunden || 60) * 1000);
    this._ragPolling = usePolling(this.fetchRagStatus, ragTakt, { sofort: false });
    this._ragPolling.start(ragTakt);

    window.addEventListener("keydown", this.handleGlobalKeydown);
    document.addEventListener("mousedown", this.handleClickOutside, true);
    // click, nicht mousedown: geschluckt werden muss dasselbe Event, das die
    // Karte auswertet. Ein gestopptes mousedown haelt den darauffolgenden
    // click nicht auf -- der Editor waere zu und die Karte trotzdem gewechselt.
    document.addEventListener("click", this.handleFocusModeClick, true);

    this.merkAnzahl = merklisteLesen().length;
    this.merkAbbestellen = merklisteAbonnieren((ids) => {
      this.merkAnzahl = ids.length;
    });
  },

  beforeUnmount() {
    // ⚠️ Von Hand: usePolling registriert onBeforeUnmount nur, wenn es aus
    // setup() gerufen wird. Diese Komponente ist Options-API — ohne diese
    // Zeile liefe der Takt nach dem Verlassen der Seite weiter.
    //
    // Die Marke davor deckt den Fall ab, dass mounted() noch in seinen awaits
    // hängt und der Poller hier schlicht noch nicht existiert.
    this._entladen = true;
    if (this._polling) this._polling.stop();
    // Der zweite Takt braucht dieselbe Zeile. Ohne sie liefe er nach dem
    // Verlassen der Seite weiter -- und zwar kumulativ bei jedem erneuten
    // Betreten, genau die Fehlerklasse vom 18.08.
    if (this._ragPolling) this._ragPolling.stop();
    window.removeEventListener("keydown", this.handleGlobalKeydown);
    document.removeEventListener("mousedown", this.handleClickOutside, true);
    document.removeEventListener("click", this.handleFocusModeClick, true);

    if (this.merkAbbestellen) this.merkAbbestellen();

    if (this.noticeTimer) {
      clearTimeout(this.noticeTimer);
      this.noticeTimer = null;
    }
  },
};
</script>

<style scoped>
/* Haelt eine angesprungene Karte unter der klebenden Kriterienleiste. Ohne das
   verschwindet sie beim Anspringen zur Haelfte darunter - scrollIntoView kennt
   sticky-Elemente nicht, scroll-margin ist der einzige Hebel dagegen. */
.messages-flow > * {
  scroll-margin-top: 4.5rem;
  scroll-margin-bottom: 1rem;
}

/* Fokus-Modus waehrend einer manuellen Korrektur: alles ausser der bearbeiteten
   Karte tritt zurueck.

   ACHTUNG, zwei Dinge sind hier bewusst so und nicht anders:

   1) NUR opacity, kein filter. filter (und transform, backdrop-filter, ...)
      erzeugt einen Containing Block fuer position:fixed -- ein fixiertes
      Overlay INNERHALB einer solchen Karte wuerde auf Kartengroesse schrumpfen
      statt den Viewport zu fuellen. Aus demselben Grund darf die Klasse
      niemals auf .messages-flow selbst, den Seitenrahmen oder <main>.

   2) pointer-events: none -- aber aus einem ANDEREN Grund als frueher.
      Frueher stand es hier, weil ein Klick auf eine blasse Karte
      activeMessageId umsetzte und einen verwaisten Editor hinterliess; der
      Preis war, dass ein Klick daneben ueberhaupt nichts tat. Das erledigt
      seit dem 08.08. handleFocusModeClick, und zwar unabhaengig davon: der
      Handler haengt am document und in der Capture-Phase, er braucht die
      blasse Karte gar nicht als Ereignisziel. Trifft der Klick sie, laeuft er
      auf .messages-flow durch -- das liegt ebenfalls ausserhalb der
      bearbeiteten Karte, und der Fokus endet genauso.
      Geblieben ist die Zeile fuer die MAUSZEIGER-Rueckmeldung (Simon, 08.08.):
      ohne sie leuchten Knoepfe in fokusfremden Karten beim Ueberfahren auf und
      der Cursor wird zur Hand. Das ist eine Zusage -- "hier kannst du etwas
      ausloesen" --, und sie stimmt im Fokus-Modus nicht: der Klick dort
      beendet den Fokus und tut sonst nichts. */
.messages-flow .card-dimmed {
  opacity: 0.38;
  pointer-events: none;
}

@media (prefers-reduced-motion: reduce) {
  .messages-flow .card-dimmed {
    transition: none;
  }
}
</style>
